import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  CheckCircle2, PackageCheck, RefreshCw, Search, ShieldCheck, Truck,
  X as XIcon, ExternalLink, ChevronDown, DollarSign, Package, Clock,
  CheckCheck, BarChart3, Users, Tag, Tags, Trash2, Plus, Pencil,
  Zap, Printer, Download, Loader2, Ship, FileCheck2, Archive, ArchiveRestore, Sparkles,
  Mail, Ban, Leaf,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { statusPillClass, statusLabel, CARRIER_OPTIONS } from "../data/orderStatus";
import CoaRequestsTab from "./admin/CoaRequestsTab";
import NewsletterTab from "./admin/NewsletterTab";
import AffiliatesTab from "./admin/AffiliatesTab";
import CompoundsTab from "./admin/CompoundsTab";
import ManualOrderModal from "./admin/ManualOrderModal";
import EditOrderItemsModal from "./admin/EditOrderItemsModal";
import VoidManualOrderModal from "./admin/VoidManualOrderModal";
import OrderDetailModal from "./admin/OrderDetailModal";
import SalesTab from "./admin/SalesTab";
import BulkStockPaste from "./admin/BulkStockPaste";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_OPTIONS = [
  { key: "all",       label: "All Orders",              filter: null },
  { key: "pending",   label: "Pending Payment - Zelle", filter: "Pending Payment - Zelle" },
  { key: "confirmed", label: "Payment Confirmed",       filter: "Payment Confirmed" },
  { key: "shipped",   label: "Shipped / Fulfilled",     filter: "Shipped / Fulfilled" },
];

const usd = (n) =>
  n == null ? "—" : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function Admin() {
  const { token, user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  // Persist active section to URL searchParams (?tab=pricing) so HMR
  // re-renders (triggered by the pricing manager rewriting products.js)
  // don't bounce the admin back to the Orders tab.
  const SECTION_VALUES = ["orders", "customers", "inventory", "compounds", "discounts", "sales", "promo-slides", "pricing", "shipping", "coa", "newsletter", "affiliates"];
  const rawTab = searchParams.get("tab");
  const section = SECTION_VALUES.includes(rawTab) ? rawTab : "orders";
  const setSection = (next) => {
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      if (next === "orders") p.delete("tab"); else p.set("tab", next);
      return p;
    }, { replace: true });
  };
  const [activeKey, setActiveKey] = useState("all");
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [acting, setActing] = useState("");
  const [stats, setStats] = useState(null);
  // order_number of the order whose "Mark Fulfilled" tracking form is open.
  const [fulfillingFor, setFulfillingFor] = useState("");
  // order_number whose Buy & Print Label modal is open (null = none).
  const [buyLabelFor, setBuyLabelFor] = useState(null);
  // Which order view is active — `active` (default), `archived`, or
  // `deleted` (Recently Deleted trash bin). Kept as a single string so
  // the three modes are mutually exclusive.
  const [orderView, setOrderView] = useState("active");
  // Bulk "Delete all test orders" flash message ("" | "loading" | "N deleted").
  const [bulkDeleteMsg, setBulkDeleteMsg] = useState("");
  // Order-numbers currently ticked for bulk-delete (only meaningful when
  // orderView === "archived" or "deleted"; the checkboxes only render there).
  const [selectedForDelete, setSelectedForDelete] = useState(() => new Set());
  // Manual (walk-in / phone) order creation modal open flag.
  const [manualOrderOpen, setManualOrderOpen] = useState(false);
  // Order number currently being voided/refunded (for the manual-order flow).
  // Null when no void confirmation modal is open.
  const [voidingFor, setVoidingFor] = useState(null);
  // Order number currently being line-item-edited by an admin (fixes stale
  // qty like LX-0807-4E0CE6). Null when no editor modal is open.
  const [editingItemsFor, setEditingItemsFor] = useState(null);

  const fetchStats = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStats(data);
    } catch {
      // non-fatal: stats card row will just show dashes
    }
  }, [token]);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const filter = STATUS_OPTIONS.find((o) => o.key === activeKey)?.filter;
      const params = new URLSearchParams();
      if (filter) params.set("status", filter);
      if (orderView === "archived") params.set("archived", "true");
      if (orderView === "deleted") params.set("deleted", "true");
      const qs = params.toString();
      const url = `${API}/admin/orders${qs ? `?${qs}` : ""}`;
      const { data } = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOrders(data || []);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load orders");
    } finally {
      setLoading(false);
    }
  }, [activeKey, token, orderView]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);
  useEffect(() => { fetchStats(); }, [fetchStats]);

  // Any time the view or status filter changes, drop any stale
  // selection so we don't accidentally bulk-delete something the admin
  // can no longer see.
  useEffect(() => {
    setSelectedForDelete(new Set());
  }, [orderView, activeKey]);

  const toggleSelectForDelete = useCallback((orderNumber) => {
    setSelectedForDelete((prev) => {
      const next = new Set(prev);
      if (next.has(orderNumber)) next.delete(orderNumber);
      else next.add(orderNumber);
      return next;
    });
  }, []);

  const clearSelection = useCallback(() => setSelectedForDelete(new Set()), []);

  const selectAllVisible = useCallback(() => {
    setSelectedForDelete((prev) => {
      const next = new Set(prev);
      orders.forEach((o) => next.add(o.order_number));
      return next;
    });
  }, [orders]);

  const patchStatus = async (orderNumber, body) => {
    setActing(orderNumber);
    try {
      const { data } = await axios.patch(
        `${API}/admin/orders/${orderNumber}/status`,
        body,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setOrders((prev) =>
        prev.map((o) => (o.order_number === orderNumber ? data : o)),
      );
      // Refresh stats so the revenue + counts cards update in real time.
      fetchStats();
      setFulfillingFor("");
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not update status");
    } finally {
      setActing("");
    }
  };

  const confirmPayment = (orderNumber) =>
    patchStatus(orderNumber, { status: "Payment Confirmed" });

  const submitFulfill = (orderNumber, carrier, trackingNumber) =>
    patchStatus(orderNumber, {
      status: "Shipped / Fulfilled",
      carrier,
      tracking_number: trackingNumber,
    });

  const toggleArchive = async (orderNumber, nextArchived) => {
    setActing(orderNumber);
    try {
      const { data } = await axios.patch(
        `${API}/admin/orders/${orderNumber}/archive`,
        { archived: nextArchived },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      // If the order's new archived flag no longer matches the current
      // view (Active shows non-archived; Archived shows archived), drop it
      // from the visible list. Otherwise update the row in place.
      const viewingArchived = orderView === "archived";
      if (data.archived !== viewingArchived) {
        setOrders((prev) => prev.filter((o) => o.order_number !== orderNumber));
      } else {
        setOrders((prev) =>
          prev.map((o) => (o.order_number === orderNumber ? data : o)),
        );
      }
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not archive order");
    } finally {
      setActing("");
    }
  };

  const deleteOrder = async (orderNumber) => {
    const ok = window.confirm(
      `Are you sure you want to permanently delete order ${orderNumber}?\n\n` +
      `The order will move to Recently Deleted and be permanently purged after 7 days. ` +
      `You can restore it any time before then.`
    );
    if (!ok) return;
    setActing(orderNumber);
    try {
      await axios.delete(`${API}/admin/orders/${orderNumber}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOrders((prev) => prev.filter((o) => o.order_number !== orderNumber));
      setSelectedForDelete((prev) => {
        const next = new Set(prev); next.delete(orderNumber); return next;
      });
      toast.success(`Order ${orderNumber} moved to Recently Deleted`);
      fetchStats();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not delete order";
      setError(msg);
      toast.error(msg);
    } finally {
      setActing("");
    }
  };

  const restoreOrder = async (orderNumber) => {
    setActing(orderNumber);
    try {
      await axios.post(
        `${API}/admin/orders/${orderNumber}/restore`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setOrders((prev) => prev.filter((o) => o.order_number !== orderNumber));
      toast.success(`Order ${orderNumber} restored`);
      fetchStats();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not restore order");
    } finally {
      setActing("");
    }
  };

  const permanentDeleteOrder = async (orderNumber) => {
    const ok = window.confirm(
      `Permanently delete order ${orderNumber} right now?\n\n` +
      `This skips the 7-day trash window and cannot be undone.`
    );
    if (!ok) return;
    setActing(orderNumber);
    try {
      await axios.delete(
        `${API}/admin/orders/${orderNumber}/permanent`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setOrders((prev) => prev.filter((o) => o.order_number !== orderNumber));
      setSelectedForDelete((prev) => {
        const next = new Set(prev); next.delete(orderNumber); return next;
      });
      toast.success(`Order ${orderNumber} permanently deleted`);
      fetchStats();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not permanently delete order");
    } finally {
      setActing("");
    }
  };

  const bulkDeleteSelected = async () => {
    const numbers = Array.from(selectedForDelete);
    if (numbers.length === 0) return;
    const isTrashView = orderView === "deleted";
    const ok = window.confirm(
      isTrashView
        ? `Permanently delete ${numbers.length} order${numbers.length === 1 ? "" : "s"} right now?\n\n` +
          `This skips the 7-day trash window and cannot be undone.`
        : `Are you sure you want to permanently delete ${numbers.length} order${numbers.length === 1 ? "" : "s"}?\n\n` +
          `They will move to Recently Deleted and be permanently purged after 7 days. ` +
          `You can restore them any time before then.`
    );
    if (!ok) return;
    setBulkDeleteMsg("loading");
    try {
      const endpoint = isTrashView
        ? `${API}/admin/orders/bulk-permanent-delete`
        : `${API}/admin/orders/bulk-delete`;
      const { data } = await axios.post(
        endpoint,
        { order_numbers: numbers },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setBulkDeleteMsg(
        `${data.deleted_count} ${isTrashView ? "purged" : "deleted"}${data.skipped_count ? `, ${data.skipped_count} skipped` : ""}`,
      );
      if (data.deleted_count) {
        toast.success(
          isTrashView
            ? `Permanently deleted ${data.deleted_count} order${data.deleted_count === 1 ? "" : "s"}`
            : `Moved ${data.deleted_count} order${data.deleted_count === 1 ? "" : "s"} to Recently Deleted`,
        );
      }
      if (data.skipped_count) {
        toast.error(
          `${data.skipped_count} skipped — ${isTrashView ? "only orders already in Recently Deleted can be permanently removed." : "only archived / cancelled / refunded / test orders can be deleted."}`,
        );
      }
      clearSelection();
      fetchOrders();
      fetchStats();
      window.setTimeout(() => setBulkDeleteMsg(""), 3500);
    } catch (e) {
      setBulkDeleteMsg("");
      toast.error(e?.response?.data?.detail || "Bulk delete failed");
    }
  };

  const deleteAllTestOrders = async () => {
    const ok = window.confirm(
      `Delete ALL Stripe test-card orders?\n\n` +
      `This will permanently remove every order paid with a Stripe test key. ` +
      `Real (live-mode) orders and Zelle orders are unaffected. This cannot be undone.`
    );
    if (!ok) return;
    setBulkDeleteMsg("loading");
    try {
      const { data } = await axios.post(
        `${API}/admin/orders/delete-test-orders`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setBulkDeleteMsg(`${data.deleted} test order${data.deleted === 1 ? "" : "s"} deleted.`);
      fetchOrders();
      fetchStats();
      // Auto-clear the flash after 5s.
      setTimeout(() => setBulkDeleteMsg(""), 5000);
    } catch (e) {
      setBulkDeleteMsg("");
      setError(e?.response?.data?.detail || "Could not delete test orders");
    }
  };

  return (
    <main data-testid="admin-page" className="py-10 md:py-14 bg-cream-200/30 min-h-[calc(100vh-7rem)]">
      <div className="container-x">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
          <div>
            <p className="overline mb-2 flex items-center gap-2">
              <ShieldCheck className="w-3.5 h-3.5 text-[#8B2252]" strokeWidth={2} />
              Admin Dashboard
            </p>
            <h1 className="font-display text-4xl md:text-5xl font-bold text-charcoal-600 leading-tight">
              {section === "orders" ? "Order management"
                : section === "customers" ? "Customers"
                : section === "inventory" ? "Inventory"
                : section === "discounts" ? "Coupon codes"
                : section === "sales" ? "Sales"
                : section === "promo-slides" ? "Promo slides"
                : section === "shipping" ? "Shipping settings"
                : section === "newsletter" ? "Newsletter"
                : section === "affiliates" ? "Affiliate program"
                : section === "coa" ? "COA requests"
                : section === "compounds" ? "Compounds"
                : "Pricing manager"}
            </h1>
            <p className="mt-2 text-sm text-charcoal-400">
              Signed in as <span className="text-charcoal-600 font-medium">{user?.email}</span>.
              {section === "orders"
                ? " Confirm Zelle payments and ship orders with tracking here."
                : section === "customers"
                ? " Search and inspect every researcher account on the platform."
                : section === "inventory"
                ? " Adjust stock levels and watch for low-inventory alerts."
                : section === "discounts"
                ? " Create and manage promo codes researchers can apply at checkout."
                : section === "sales"
                ? " Schedule automatic % off sales on chosen products. One sale runs at a time."
                : section === "shipping"
                ? " Adjust standard shipping price and the free-shipping threshold. Changes go live instantly."
                : section === "newsletter"
                ? " Compose and send marketing emails to opted-in customers via Resend. Each email includes a one-click unsubscribe link."
                : section === "affiliates"
                ? " Manage active affiliates, view referral orders, and process biweekly commission payouts."
                : section === "compounds"
                ? " Add, edit, hide, or remove any research compound and its sizes. Changes are live immediately."
                : " Update SKU prices — changes hot-reload across the live catalog."}
            </p>
          </div>
          <button
            type="button"
            onClick={() => { fetchOrders(); fetchStats(); }}
            data-testid="admin-refresh"
            className="btn-secondary self-start md:self-auto"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} strokeWidth={2} />
            Refresh
          </button>
        </div>

        {/* Revenue dashboard — always visible at the top */}
        <StatsRow stats={stats} />

        {/* Section toggle (Orders / Customers) */}
        <div
          data-testid="admin-section-tabs"
          className="mt-8 mb-6 inline-flex flex-wrap gap-y-1 p-1 bg-white border border-charcoal-100 rounded-[28px]"
        >
          <SectionTab
            active={section === "orders"} onClick={() => setSection("orders")}
            testId="admin-section-orders" icon={Package} label="Orders"
          />
          <SectionTab
            active={section === "customers"} onClick={() => setSection("customers")}
            testId="admin-section-customers" icon={Users} label="Customers"
          />
          <SectionTab
            active={section === "inventory"} onClick={() => setSection("inventory")}
            testId="admin-section-inventory" icon={Package} label="Inventory"
          />
          <SectionTab
            active={section === "discounts"} onClick={() => setSection("discounts")}
            testId="admin-section-discounts" icon={Tags} label="Coupons"
          />
          <SectionTab
            active={section === "sales"} onClick={() => setSection("sales")}
            testId="admin-section-sales" icon={Leaf} label="Sales"
          />
          <SectionTab
            active={section === "promo-slides"} onClick={() => setSection("promo-slides")}
            testId="admin-section-promo-slides" icon={Sparkles} label="Promo Slides"
          />
          <SectionTab
            active={section === "pricing"} onClick={() => setSection("pricing")}
            testId="admin-section-pricing" icon={Tag} label="Pricing"
          />
          <SectionTab
            active={section === "shipping"} onClick={() => setSection("shipping")}
            testId="admin-section-shipping" icon={Ship} label="Shipping"
          />
          <SectionTab
            active={section === "coa"} onClick={() => setSection("coa")}
            testId="admin-section-coa" icon={FileCheck2} label="COAs"
          />
          <SectionTab
            active={section === "newsletter"} onClick={() => setSection("newsletter")}
            testId="admin-section-newsletter" icon={Mail} label="Newsletter"
          />
          <SectionTab
            active={section === "affiliates"} onClick={() => setSection("affiliates")}
            testId="admin-section-affiliates" icon={Sparkles} label="Affiliates"
          />
          <SectionTab
            active={section === "compounds"} onClick={() => setSection("compounds")}
            testId="admin-section-compounds" icon={Tag} label="Compounds"
          />
        </div>

        {error && (
          <div data-testid="admin-error" className="mb-5 p-3 rounded-2xl bg-red-50 text-red-700 text-sm">
            {error}
          </div>
        )}

        {section === "orders" ? (
          <>
            {bulkDeleteMsg && (
              <div
                data-testid="admin-bulk-delete-flash"
                className="mb-5 p-3 rounded-2xl bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm font-medium"
              >
                {bulkDeleteMsg === "loading" ? "Deleting test orders…" : bulkDeleteMsg}
              </div>
            )}
            <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
              <div data-testid="admin-filter-tabs" className="flex flex-wrap gap-2 items-center">
              {STATUS_OPTIONS.map((opt) => {
                const active = opt.key === activeKey;
                return (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => setActiveKey(opt.key)}
                    data-testid={`admin-filter-${opt.key}`}
                    className={`text-sm px-4 py-2 rounded-full font-medium transition-colors ${
                      active
                        ? "bg-[#8B2252] text-white"
                        : "bg-white text-charcoal-500 border border-charcoal-100 hover:bg-cream-200"
                    }`}
                  >
                    {opt.label}
                  </button>
                );
              })}
              {!loading && (
                <span
                  data-testid="admin-orders-count"
                  className="text-xs text-charcoal-500 ml-2 px-3 py-1 rounded-full bg-white border border-charcoal-100"
                  title="If this count doesn't match the dashboard stats above, the API filter is returning a subset — flag it."
                >
                  Showing <strong className="text-[#8B2252]">{orders.length}</strong>
                  {activeKey === "all" && stats?.total_orders != null && orders.length !== stats.total_orders && (
                    <> of {stats.total_orders} <span className="text-red-600 font-semibold">— mismatch</span></>
                  )}
                </span>
              )}
              </div>
              <div className="flex flex-wrap items-center gap-3">
                {/* Manual (walk-in / phone / cash) order creation */}
                <button
                  type="button"
                  onClick={() => setManualOrderOpen(true)}
                  data-testid="admin-new-manual-order"
                  className="inline-flex items-center text-sm px-4 py-2 rounded-full font-medium border border-[#8B2252] text-[#8B2252] bg-white hover:bg-[#8B2252]/[0.05]"
                >
                  <Plus className="w-4 h-4 mr-2" strokeWidth={2} />
                  New manual order
                </button>
                {/* View selector — mutually exclusive: Active (default) /
                    Archived / Recently Deleted (trash bin). */}
                <div
                  data-testid="admin-view-selector"
                  className="inline-flex items-center rounded-full bg-white border border-charcoal-100 p-1 text-sm"
                >
                  {[
                    { key: "active",   label: "Active",           icon: Package,       testId: "admin-view-active" },
                    { key: "archived", label: "Archived",         icon: Archive,       testId: "admin-view-archived" },
                    { key: "deleted",  label: "Recently deleted", icon: Trash2,        testId: "admin-view-deleted" },
                  ].map((v) => {
                    const Ico = v.icon;
                    const active = orderView === v.key;
                    return (
                      <button
                        key={v.key}
                        type="button"
                        onClick={() => setOrderView(v.key)}
                        data-testid={v.testId}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full transition-colors ${
                          active
                            ? "bg-charcoal-600 text-cream-200"
                            : "text-charcoal-500 hover:bg-cream-200"
                        }`}
                      >
                        <Ico className="w-4 h-4" strokeWidth={2} />
                        {v.label}
                      </button>
                    );
                  })}
                </div>
                {/* Bulk delete test orders */}
                <button
                  type="button"
                  onClick={deleteAllTestOrders}
                  disabled={bulkDeleteMsg === "loading"}
                  data-testid="admin-delete-test-orders"
                  className="inline-flex items-center text-sm px-4 py-2 rounded-full font-medium border border-red-200 text-red-700 bg-white hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Trash2 className="w-4 h-4 mr-2" strokeWidth={2} />
                  Delete all test orders
                </button>
                <a
                  href={`${API}/admin/orders/export.csv`}
                  onClick={async (e) => {
                    // Fetch with auth header, build blob, then trigger download.
                    e.preventDefault();
                    const res = await fetch(`${API}/admin/orders/export.csv`, {
                      headers: { Authorization: `Bearer ${token}` },
                    });
                    const blob = await res.blob();
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url; a.download = "luviarx_orders.csv";
                    document.body.appendChild(a); a.click();
                    a.remove(); URL.revokeObjectURL(url);
                  }}
                  data-testid="admin-export-csv"
                  className="btn-secondary"
                >
                  Export CSV
                </a>
              </div>
            </div>

            {loading ? (
              <div className="surface-card p-10 text-center text-charcoal-400 text-sm">Loading orders…</div>
            ) : orders.length === 0 ? (
              <div data-testid="admin-empty" className="surface-card p-10 text-center">
                <Search className="w-7 h-7 text-charcoal-300 mx-auto mb-3" strokeWidth={1.5} />
                <p className="font-display text-lg text-charcoal-600 mb-1">
                  {orderView === "deleted"
                    ? "Recently Deleted is empty."
                    : orderView === "archived"
                    ? "No archived orders."
                    : "No orders match this filter."}
                </p>
                <p className="text-sm text-charcoal-400">
                  {orderView === "deleted"
                    ? "Orders you delete land here for 7 days before being permanently purged."
                    : orderView === "archived"
                    ? "Switch back to Active to see live orders."
                    : "Try a different filter or wait for new orders to come in."}
                </p>
              </div>
            ) : (
              <div className="space-y-4">
            {/* Bulk selection bar — visible in Archived + Recently Deleted
                views. In Recently Deleted the bulk action becomes a hard
                permanent purge; elsewhere it's a soft-delete to the trash. */}
            {(orderView === "archived" || orderView === "deleted") && (
              <div
                data-testid="admin-bulk-select-header"
                className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 rounded-2xl border"
                style={{
                  backgroundColor: selectedForDelete.size > 0 ? "#F5EAE6" : "#FDFAF9",
                  borderColor: selectedForDelete.size > 0
                    ? "rgba(139, 34, 82, 0.35)"
                    : "rgba(28, 22, 24, 0.08)",
                }}
              >
                <label className="inline-flex items-center gap-2 cursor-pointer text-sm text-charcoal-600">
                  <input
                    type="checkbox"
                    data-testid="admin-bulk-select-all"
                    checked={selectedForDelete.size > 0 && selectedForDelete.size >= orders.length}
                    onChange={(e) =>
                      e.target.checked ? selectAllVisible() : clearSelection()
                    }
                    className="w-4 h-4 accent-[#8B2252] cursor-pointer"
                  />
                  <span>
                    {selectedForDelete.size > 0 ? (
                      <>
                        <strong
                          data-testid="admin-bulk-selection-count"
                          style={{ color: "#8B2252" }}
                        >
                          {selectedForDelete.size}
                        </strong>{" "}
                        of {orders.length} selected
                      </>
                    ) : (
                      <>Select all {orderView === "deleted" ? "trashed" : "archived"} orders on this page</>
                    )}
                  </span>
                </label>
                <div className="flex items-center gap-2">
                  {bulkDeleteMsg && bulkDeleteMsg !== "loading" && (
                    <span
                      data-testid="admin-bulk-delete-flash"
                      className="text-xs text-charcoal-500"
                    >
                      {bulkDeleteMsg}
                    </span>
                  )}
                  {selectedForDelete.size > 0 && (
                    <>
                      <button
                        type="button"
                        onClick={clearSelection}
                        disabled={bulkDeleteMsg === "loading"}
                        data-testid="admin-bulk-clear"
                        className="text-xs text-charcoal-500 hover:text-charcoal-700 underline underline-offset-4 disabled:opacity-50"
                      >
                        Clear
                      </button>
                      <button
                        type="button"
                        onClick={bulkDeleteSelected}
                        disabled={bulkDeleteMsg === "loading"}
                        data-testid="admin-bulk-delete"
                        className="inline-flex items-center gap-2 text-sm px-4 py-2 rounded-full font-semibold text-white transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                        style={{ backgroundColor: "#8B2252" }}
                      >
                        <Trash2 className="w-4 h-4" strokeWidth={2} />
                        {bulkDeleteMsg === "loading"
                          ? "Deleting…"
                          : orderView === "deleted"
                          ? `Permanently delete ${selectedForDelete.size} selected`
                          : `Delete ${selectedForDelete.size} selected`}
                      </button>
                    </>
                  )}
                </div>
              </div>
            )}
            {orders.map((o) => (
              <OrderCard
                key={o.order_number}
                order={o}
                acting={acting === o.order_number}
                fulfilling={fulfillingFor === o.order_number}
                onConfirm={() => confirmPayment(o.order_number)}
                onStartFulfill={() => setFulfillingFor(o.order_number)}
                onCancelFulfill={() => setFulfillingFor("")}
                onSubmitFulfill={(carrier, tn) => submitFulfill(o.order_number, carrier, tn)}
                onOpenBuyLabel={(orderNumber) => setBuyLabelFor(orderNumber)}
                onToggleArchive={(next) => toggleArchive(o.order_number, next)}
                onDelete={() => deleteOrder(o.order_number)}
                onRestore={() => restoreOrder(o.order_number)}
                onPermanentDelete={() => permanentDeleteOrder(o.order_number)}
                onVoidManual={() => setVoidingFor(o.order_number)}
                onEditItems={() => setEditingItemsFor(o.order_number)}
                selectable={orderView === "archived" || orderView === "deleted"}
                selected={selectedForDelete.has(o.order_number)}
                onSelectToggle={() => toggleSelectForDelete(o.order_number)}
                trashMode={orderView === "deleted"}
              />
            ))}
          </div>
        )}
        {buyLabelFor && (
          <BuyLabelModal
            token={token}
            order={orders.find((o) => o.order_number === buyLabelFor)}
            onClose={() => setBuyLabelFor(null)}
            onPurchased={(updated) => {
              setOrders((prev) =>
                prev.map((o) => (o.order_number === updated.order_number ? updated : o)),
              );
              fetchStats();
              setBuyLabelFor(null);
              // Open the label PDF in a new tab so it can be sent to a thermal printer.
              if (updated.easypost_postage_label_url) {
                window.open(updated.easypost_postage_label_url, "_blank", "noopener");
              }
            }}
          />
        )}
        {manualOrderOpen && (
          <ManualOrderModal
            token={token}
            onClose={() => setManualOrderOpen(false)}
            onCreated={(newOrder) => {
              setManualOrderOpen(false);
              // Refetch to pick up any status-filter changes + refresh stats
              // so the new revenue is reflected immediately.
              fetchOrders();
              fetchStats();
              // Prepend the new order for instant visibility even before the
              // network refetch resolves.
              setOrders((prev) => [newOrder, ...prev.filter(
                (o) => o.order_number !== newOrder.order_number,
              )]);
            }}
          />
        )}
        {voidingFor && (
          <VoidManualOrderModal
            token={token}
            order={orders.find((o) => o.order_number === voidingFor)}
            onClose={() => setVoidingFor(null)}
            onVoided={(updated) => {
              setOrders((prev) =>
                prev.map((o) => (o.order_number === updated.order_number ? updated : o)),
              );
              fetchStats();
              setVoidingFor(null);
            }}
          />
        )}
        {editingItemsFor && (
          <EditOrderItemsModal
            token={token}
            order={orders.find((o) => o.order_number === editingItemsFor)}
            onClose={() => setEditingItemsFor(null)}
            onSaved={(updated) => {
              setOrders((prev) =>
                prev.map((o) => (o.order_number === updated.order_number ? updated : o)),
              );
              fetchStats();
              setEditingItemsFor(null);
            }}
          />
        )}
          </>
        ) : section === "customers" ? (
          <CustomersPanel token={token} />
        ) : section === "inventory" ? (
          <InventoryPanel token={token} />
        ) : section === "discounts" ? (
          <DiscountsPanel token={token} />
        ) : section === "sales" ? (
          <SalesTab token={token} />
        ) : section === "promo-slides" ? (
          <PromoSlidesPanel token={token} />
        ) : section === "pricing" ? (
          <PricingPanel token={token} />
        ) : section === "coa" ? (
          <CoaRequestsTab token={token} />
        ) : section === "newsletter" ? (
          <NewsletterTab token={token} />
        ) : section === "affiliates" ? (
          <AffiliatesTab token={token} />
        ) : section === "compounds" ? (
          <CompoundsTab token={token} />
        ) : (
          <ShippingSettingsPanel token={token} />
        )}
      </div>
    </main>
  );
}

// ---------- Stats row (revenue dashboard) ----------
const StatsRow = ({ stats }) => (
  <div
    data-testid="admin-stats-row"
    className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4"
  >
    <StatCard
      icon={DollarSign} testId="stat-total-revenue"
      label="Total revenue" value={usd(stats?.total_revenue ?? 0)}
      hint="Confirmed + shipped"
    />
    <StatCard
      icon={Package} testId="stat-total-orders"
      label="Total orders" value={stats?.total_orders ?? 0}
      hint="All-time"
    />
    <StatCard
      icon={Clock} testId="stat-pending-orders"
      label="Pending orders" value={stats?.pending_orders ?? 0}
      hint="Awaiting Zelle"
    />
    <StatCard
      icon={CheckCheck} testId="stat-fulfilled-orders"
      label="Fulfilled orders" value={stats?.fulfilled_orders ?? 0}
      hint="Shipped"
    />
    <StatCard
      icon={BarChart3} testId="stat-aov"
      label="Avg order value" value={usd(stats?.avg_order_value ?? 0)}
      hint="Revenue / paid orders"
    />
  </div>
);

const StatCard = ({ icon: Icon, label, value, hint, testId }) => (
  <div
    data-testid={testId}
    className="rounded-2xl p-5 border border-charcoal-100/60"
    style={{ backgroundColor: "#FDFAF9" }}
  >
    <div className="flex items-start justify-between mb-3">
      <p className="text-[10px] uppercase tracking-[0.18em] text-charcoal-400 font-medium">
        {label}
      </p>
      <div className="w-8 h-8 rounded-full bg-[#8B2252]/10 flex items-center justify-center">
        <Icon className="w-4 h-4 text-[#8B2252]" strokeWidth={1.8} />
      </div>
    </div>
    <p className="font-display font-bold text-3xl text-[#8B2252] leading-none">
      {value}
    </p>
    {hint && <p className="text-[11px] text-charcoal-300 mt-2">{hint}</p>}
  </div>
);

// ---------- Section tab button ----------
const SectionTab = ({ active, onClick, icon: Icon, label, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className={`inline-flex items-center whitespace-nowrap text-sm font-medium px-4 py-2 rounded-full transition-colors ${
      active ? "bg-[#8B2252] text-white" : "text-charcoal-500 hover:bg-cream-200"
    }`}
  >
    <Icon className="w-4 h-4 mr-1.5" strokeWidth={1.8} />
    {label}
  </button>
);

// ---------- Customers panel ----------
const CustomersPanel = ({ token }) => {
  const [customers, setCustomers] = useState(null);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [openCustomer, setOpenCustomer] = useState(null);
  const [discountFor, setDiscountFor] = useState(null);
  const [affiliateFor, setAffiliateFor] = useState(null);

  const applyDiscountUpdate = (updated) => {
    setCustomers((prev) =>
      (prev || []).map((c) =>
        c.id === updated.id
          ? {
              ...c,
              personal_discount_active: updated.personal_discount_active,
              personal_discount_pct: updated.personal_discount_pct,
              personal_discount_label: updated.personal_discount_label,
            }
          : c,
      ),
    );
  };

  useEffect(() => {
    let cancelled = false;
    const handle = setTimeout(async () => {
      try {
        setError("");
        const url = search.trim()
          ? `${API}/admin/customers?q=${encodeURIComponent(search.trim())}`
          : `${API}/admin/customers`;
        const { data } = await axios.get(url, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setCustomers(data || []);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || "Failed to load customers");
      }
    }, search ? 250 : 0); // small debounce for typing
    return () => { cancelled = true; clearTimeout(handle); };
  }, [token, search]);

  return (
    <div data-testid="admin-customers-panel">
      <div className="mb-6 flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 text-charcoal-400 absolute left-3 top-1/2 -translate-y-1/2" strokeWidth={2} />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or email…"
            data-testid="admin-customer-search"
            className="input pl-10"
          />
        </div>
        <button
          type="button"
          onClick={async () => {
            const res = await fetch(`${API}/admin/customers/export.csv`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) return;
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url; a.download = "luviarx_customers.csv";
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(url);
          }}
          data-testid="admin-customers-export-csv"
          className="btn-secondary whitespace-nowrap"
        >
          <Download className="w-4 h-4 mr-2" strokeWidth={2} />
          Export CSV
        </button>
      </div>

      {error && (
        <div data-testid="admin-customers-error" className="mb-4 p-3 rounded-2xl bg-red-50 text-red-700 text-sm">
          {error}
        </div>
      )}

      {customers === null ? (
        <div className="surface-card p-10 text-center text-charcoal-400 text-sm">Loading customers…</div>
      ) : customers.length === 0 ? (
        <div data-testid="admin-customers-empty" className="surface-card p-10 text-center">
          <Users className="w-7 h-7 text-charcoal-300 mx-auto mb-3" strokeWidth={1.5} />
          <p className="font-display text-lg text-charcoal-600 mb-1">No customers match your search.</p>
        </div>
      ) : (
        <div className="surface-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm" data-testid="admin-customers-table">
              <thead className="bg-cream-200/70 text-charcoal-500">
                <tr>
                  <Th>Name</Th>
                  <Th>Email</Th>
                  <Th>Research field</Th>
                  <Th>Registered</Th>
                  <Th className="text-right">Orders</Th>
                  <Th className="text-right">Spent</Th>
                  <Th>Personal discount</Th>
                  <Th>Affiliate</Th>
                  <Th className="text-right">Actions</Th>
                </tr>
              </thead>
              <tbody>
                {customers.map((c) => (
                  <tr
                    key={c.id}
                    data-testid={`customer-row-${c.id}`}
                    className="border-t border-charcoal-100/40 hover:bg-cream-200/50 transition-colors"
                  >
                    <Td>
                      <span className="font-medium text-charcoal-600">
                        {(c.first_name || "—") + " " + (c.last_name || "")}
                      </span>
                      {c.is_admin && (
                        <span className="ml-2 text-[10px] uppercase tracking-wider bg-[#8B2252] text-white px-2 py-0.5 rounded-full">
                          Admin
                        </span>
                      )}
                    </Td>
                    <Td className="text-charcoal-500 break-all">{c.email}</Td>
                    <Td>{c.research_field || "—"}</Td>
                    <Td className="text-charcoal-400">
                      {c.created_at ? new Date(c.created_at).toLocaleDateString() : "—"}
                    </Td>
                    <Td className="text-right font-medium text-charcoal-600">{c.order_count}</Td>
                    <Td className="text-right font-display font-bold text-[#8B2252]">
                      {usd(c.total_spent)}
                    </Td>
                    <Td>
                      <CustomerDiscountBadge customer={c} />
                    </Td>
                    <Td>
                      <CustomerAffiliateBadge customer={c} />
                    </Td>
                    <Td className="text-right whitespace-nowrap">
                      <button
                        type="button"
                        onClick={() => setDiscountFor(c)}
                        data-testid={`customer-edit-discount-${c.id}`}
                        className="text-sm font-medium text-charcoal-500 hover:text-[#8B2252] hover:underline mr-4"
                      >
                        Discount
                      </button>
                      <button
                        type="button"
                        onClick={() => setAffiliateFor(c)}
                        data-testid={`customer-affiliate-btn-${c.id}`}
                        className="text-sm font-medium text-charcoal-500 hover:text-[#8B2252] hover:underline mr-4"
                      >
                        Affiliate
                      </button>
                      <button
                        type="button"
                        onClick={() => setOpenCustomer(c)}
                        data-testid={`customer-view-orders-${c.id}`}
                        className="text-sm font-medium text-[#8B2252] hover:underline"
                      >
                        View orders →
                      </button>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {openCustomer && (
        <CustomerOrdersDrawer
          token={token}
          customer={openCustomer}
          onClose={() => setOpenCustomer(null)}
        />
      )}

      {discountFor && (
        <CustomerDiscountModal
          token={token}
          customer={discountFor}
          onClose={() => setDiscountFor(null)}
          onSaved={(updated) => {
            applyDiscountUpdate(updated);
            setDiscountFor(null);
          }}
        />
      )}

      {affiliateFor && (
        <CustomerAffiliateModal
          token={token}
          customer={affiliateFor}
          onClose={() => setAffiliateFor(null)}
          onSaved={(updated) => {
            setCustomers((prev) =>
              (prev || []).map((c) =>
                c.id === updated.id ? { ...c, ...updated } : c,
              ),
            );
            setAffiliateFor(null);
          }}
        />
      )}
    </div>
  );
};

// Small pill that shows a customer's current personal-discount state in
// the customers table. "—" when inactive/unset; the label + %off when active.
const CustomerDiscountBadge = ({ customer }) => {
  const pct = Number(customer.personal_discount_pct) || 0;
  const active = !!customer.personal_discount_active && pct > 0;
  const label = (customer.personal_discount_label || "").trim();
  if (!active) {
    if (pct > 0 && label) {
      return (
        <span
          data-testid={`customer-discount-badge-${customer.id}`}
          className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-1 rounded-full bg-charcoal-100 text-charcoal-400"
          title={`Inactive — ${pct}% off "${label}" saved`}
        >
          Inactive · {pct}%
        </span>
      );
    }
    return (
      <span
        data-testid={`customer-discount-badge-${customer.id}`}
        className="text-charcoal-300 text-sm"
      >
        —
      </span>
    );
  }
  return (
    <span
      data-testid={`customer-discount-badge-${customer.id}`}
      className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-1 rounded-full"
      style={{ background: "rgba(139,34,82,0.10)", color: "#8B2252" }}
      title={label || "Personal discount"}
    >
      {label || "Personal"} · {pct}% off
    </span>
  );
};

// ---- Affiliate badge + modal ----
const CustomerAffiliateBadge = ({ customer }) => {
  if (!customer.is_affiliate) {
    return (
      <span
        data-testid={`customer-affiliate-badge-${customer.id}`}
        className="text-charcoal-300 text-sm"
      >
        —
      </span>
    );
  }
  const code = customer.referral_code || "";
  const pct = Number(customer.affiliate_personal_discount_pct) || 0;
  const bits = [];
  if (pct > 0) bits.push(`${pct}% self`);
  if (code) bits.push(code);
  return (
    <span
      data-testid={`customer-affiliate-badge-${customer.id}`}
      className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-1 rounded-full"
      style={{ background: "rgba(139,34,82,0.10)", color: "#8B2252" }}
      title={`Affiliate — code: ${code || "none"}, self-discount: ${pct}%`}
    >
      <Sparkles className="w-3 h-3" strokeWidth={2} />
      {bits.join(" · ") || "Active"}
    </span>
  );
};

const CustomerAffiliateModal = ({ token, customer, onClose, onSaved }) => {
  const [coupons, setCoupons] = React.useState(null);
  const [isAff, setIsAff] = React.useState(!!customer.is_affiliate);
  const [pct, setPct] = React.useState(
    String(Number(customer.affiliate_personal_discount_pct) || 0),
  );
  const [selectedCode, setSelectedCode] = React.useState(customer.referral_code || "");
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/admin/discounts`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(({ data }) => {
        if (cancelled) return;
        setCoupons((data || []).filter((c) => c.active));
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e?.response?.data?.detail || "Could not load coupons");
        setCoupons([]);
      });
    return () => { cancelled = true; };
  }, [token]);

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      const pctNum = Math.max(0, Math.min(100, parseInt(pct, 10) || 0));
      const payload = {
        is_affiliate: isAff,
        personal_discount_pct: pctNum,
        referral_code: selectedCode,
      };
      const { data } = await axios.post(
        `${API}/admin/customers/${customer.id}/affiliate`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      onSaved({
        id: data.user_id,
        is_affiliate: data.is_affiliate,
        affiliate_personal_discount_pct: data.affiliate_personal_discount_pct,
        referral_code: data.referral_code,
      });
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not save affiliate settings");
    } finally {
      setSaving(false);
    }
  };

  const clearAll = async () => {
    setSaving(true);
    setError("");
    try {
      await axios.delete(
        `${API}/admin/customers/${customer.id}/affiliate`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      onSaved({
        id: customer.id,
        is_affiliate: false,
        affiliate_personal_discount_pct: 0,
        referral_code: "",
      });
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not clear affiliate settings");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-charcoal-800/40 backdrop-blur-sm flex items-start justify-center p-4 pt-12 overflow-y-auto"
      onClick={onClose}
      data-testid="customer-affiliate-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-3xl shadow-xl w-full max-w-lg p-6 md:p-8"
      >
        <h3 className="font-display text-2xl font-bold text-charcoal-700 mb-1">
          Affiliate account
        </h3>
        <p className="text-sm text-charcoal-500 mb-5">
          {customer.first_name} {customer.last_name} · {customer.email}
        </p>

        {error && (
          <div className="mb-4 p-3 rounded-2xl bg-red-50 border border-red-100 text-red-700 text-sm">
            {error}
          </div>
        )}

        <label className="flex items-center gap-2 text-sm text-charcoal-700 font-medium mb-5">
          <input
            type="checkbox"
            checked={isAff}
            onChange={(e) => setIsAff(e.target.checked)}
            className="accent-[#8B2252]"
            data-testid="customer-affiliate-active-toggle"
          />
          Activate affiliate status
        </label>

        {/* Section 1: Personal discount */}
        <div className="rounded-2xl border border-charcoal-100 p-4 mb-4">
          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-charcoal-400 mb-2">
            Personal discount (their own purchases)
          </div>
          <label className="block text-xs text-charcoal-500 mb-1">Discount percentage (%)</label>
          <input
            type="number"
            min={0} max={100}
            value={pct}
            onChange={(e) => setPct(e.target.value)}
            className="input"
            data-testid="customer-affiliate-pct-input"
            disabled={!isAff}
            placeholder="e.g. 25"
          />
          <p className="text-xs text-charcoal-400 mt-2">
            Auto-applies to their own orders at checkout as &ldquo;Affiliate discount&rdquo;.
          </p>
        </div>

        {/* Section 2: Referral code */}
        <div className="rounded-2xl border border-charcoal-100 p-4 mb-4">
          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-charcoal-400 mb-2">
            Referral code (given to other customers)
          </div>
          <select
            value={selectedCode}
            onChange={(e) => setSelectedCode(e.target.value)}
            className="input"
            data-testid="customer-affiliate-coupon-select"
            disabled={!isAff || coupons === null}
          >
            <option value="">— none —</option>
            {(coupons || []).map((c) => {
              const disc = c.percent > 0 ? `${c.percent}% off` : `$${(c.amount_off || 0).toFixed(2)} off`;
              return (
                <option key={c.code} value={c.code}>{`${c.code} · ${disc}`}</option>
              );
            })}
          </select>
          <p className="text-xs text-charcoal-400 mt-2">
            Commission % = the coupon&apos;s discount %. Create new codes in the Coupons tab.
          </p>
        </div>

        <div className="mt-6 flex flex-col gap-2">
          <button
            type="button"
            data-testid="customer-affiliate-save"
            onClick={save}
            disabled={saving}
            className="btn-primary w-full disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {saving ? "Saving…" : "Save affiliate settings"}
          </button>
          {customer.is_affiliate && (
            <button
              type="button"
              data-testid="customer-affiliate-clear"
              onClick={clearAll}
              disabled={saving}
              className="text-xs text-red-600 hover:text-red-800 mt-1 self-center"
            >
              Clear all affiliate settings
            </button>
          )}
          <button type="button" onClick={onClose} className="btn-secondary w-full mt-1">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

// Modal for editing a single customer's personal discount. Fields:
// active toggle, integer % (0..90), free-text label. Server enforces
// the same rules. Deactivating without changing pct preserves the value.
// Also fetches and renders a chronological audit log of every change.
const CustomerDiscountModal = ({ token, customer, onClose, onSaved }) => {
  const [active, setActive] = useState(!!customer.personal_discount_active);
  const [pct, setPct] = useState(
    String(Number(customer.personal_discount_pct) || 0),
  );
  const [label, setLabel] = useState(customer.personal_discount_label || "");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [history, setHistory] = useState(null);      // null=loading, []=empty
  const [historyErr, setHistoryErr] = useState("");

  const loadHistory = useCallback(async () => {
    setHistoryErr("");
    try {
      const { data } = await axios.get(
        `${API}/admin/customers/${customer.id}/discount-history`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setHistory(Array.isArray(data) ? data : []);
    } catch (e) {
      setHistoryErr(e?.response?.data?.detail || "Could not load history");
      setHistory([]);
    }
  }, [customer.id, token]);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const submit = async (e) => {
    e?.preventDefault?.();
    setErr("");
    const pctNum = Number(pct);
    if (!Number.isFinite(pctNum) || pctNum < 0 || pctNum > 90) {
      setErr("Percentage must be between 0 and 90.");
      return;
    }
    setSaving(true);
    try {
      const { data } = await axios.patch(
        `${API}/admin/customers/${customer.id}/discount`,
        { active, pct: Math.round(pctNum), label: label.trim() },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      // Refresh history in-place so the admin sees the new entry appear
      // before the modal closes on the parent's onSaved call.
      await loadHistory();
      onSaved({ ...customer, ...data });
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Could not save discount.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="customer-discount-modal"
      className="fixed inset-0 z-[70] flex items-center justify-center px-4 py-8 overflow-y-auto"
    >
      <div
        onClick={onClose}
        className="absolute inset-0 bg-charcoal-600/50 backdrop-blur-sm"
      />
      <form
        onSubmit={submit}
        className="relative w-full max-w-md surface-card p-6 md:p-7 shadow-2xl my-auto"
      >
        <button
          type="button"
          onClick={onClose}
          data-testid="customer-discount-modal-close"
          className="absolute top-4 right-4 text-charcoal-400 hover:text-charcoal-600"
          aria-label="Close"
        >
          <XIcon className="w-5 h-5" strokeWidth={2} />
        </button>
        <p className="overline mb-1">Personal Discount</p>
        <h3 className="font-display font-bold text-2xl text-charcoal-600 mb-1">
          {(customer.first_name || "—") + " " + (customer.last_name || "")}
        </h3>
        <p className="text-xs text-charcoal-400 mb-6 break-all">{customer.email}</p>

        <label
          htmlFor="disc-active"
          className={`flex items-center justify-between gap-4 rounded-2xl border-2 p-4 mb-4 cursor-pointer transition-colors ${
            active
              ? "border-[#8B2252] bg-[#8B2252]/[0.04]"
              : "border-charcoal-100 bg-cream-100 hover:bg-cream-200/40"
          }`}
        >
          <span>
            <span className="font-display font-bold text-charcoal-600 block">
              Discount active
            </span>
            <span className="text-xs text-charcoal-400">
              When on, applied automatically to every new order at checkout.
            </span>
          </span>
          <span
            className={`relative inline-flex h-6 w-11 shrink-0 rounded-full transition-colors ${
              active ? "bg-[#8B2252]" : "bg-charcoal-200"
            }`}
          >
            <input
              id="disc-active"
              type="checkbox"
              checked={active}
              onChange={(e) => setActive(e.target.checked)}
              data-testid="customer-discount-active-toggle"
              className="sr-only"
            />
            <span
              className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${
                active ? "translate-x-5" : "translate-x-0.5"
              }`}
            />
          </span>
        </label>

        <div className="mb-4">
          <label htmlFor="disc-pct" className="block text-xs uppercase tracking-[0.14em] text-charcoal-400 font-medium mb-1.5">
            Discount percentage
          </label>
          <div className="relative">
            <input
              id="disc-pct"
              type="number"
              min="0"
              max="90"
              step="1"
              value={pct}
              onChange={(e) => setPct(e.target.value)}
              data-testid="customer-discount-pct-input"
              className="input pr-10"
              placeholder="e.g. 15"
            />
            <span className="absolute right-4 top-1/2 -translate-y-1/2 text-charcoal-400">
              %
            </span>
          </div>
          <p className="text-[11px] text-charcoal-400 mt-1">
            Applied on top of volume + promo discounts. Max 90%.
          </p>
        </div>

        <div className="mb-6">
          <label htmlFor="disc-label" className="block text-xs uppercase tracking-[0.14em] text-charcoal-400 font-medium mb-1.5">
            Label (optional)
          </label>
          <input
            id="disc-label"
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            data-testid="customer-discount-label-input"
            className="input"
            placeholder="e.g. Friends & Family, VIP, Investor"
            maxLength={80}
          />
          <p className="text-[11px] text-charcoal-400 mt-1">
            Shown to the customer on their checkout page.
          </p>
        </div>

        {err && (
          <div
            data-testid="customer-discount-error"
            className="mb-4 p-3 rounded-2xl bg-red-50 text-red-700 text-sm"
          >
            {err}
          </div>
        )}

        <div className="flex gap-3">
          <button
            type="button"
            onClick={onClose}
            data-testid="customer-discount-cancel"
            className="btn-secondary flex-1"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            data-testid="customer-discount-save"
            className="btn-primary flex-1 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </div>

        {/* Discount history — mini timeline. Ordered oldest → newest at
            top so the reader gets a story from "set" → "changed" → … */}
        <div className="mt-7 pt-6 border-t border-charcoal-100">
          <p className="overline mb-3 flex items-center gap-2">
            <Clock className="w-3.5 h-3.5 text-charcoal-400" strokeWidth={2} />
            Discount history
          </p>
          {historyErr && (
            <p className="text-xs text-red-700">{historyErr}</p>
          )}
          {history === null ? (
            <p className="text-xs text-charcoal-400">Loading…</p>
          ) : history.length === 0 ? (
            <p
              data-testid="customer-discount-history-empty"
              className="text-xs text-charcoal-400 italic"
            >
              No changes yet. This customer&apos;s first discount setup will show here.
            </p>
          ) : (
            <ul
              data-testid="customer-discount-history"
              className="space-y-3 max-h-56 overflow-y-auto pr-1"
            >
              {history.map((e) => (
                <DiscountAuditRow key={e.id} entry={e} />
              ))}
            </ul>
          )}
        </div>
      </form>
    </div>
  );
};

// A single row in the discount audit timeline.
const DiscountAuditRow = ({ entry }) => {
  const actionMeta = {
    set:         { color: "#3f7c59", bg: "rgba(63,124,89,0.10)", label: "Set" },
    changed:     { color: "#8B2252", bg: "rgba(139,34,82,0.10)", label: "Changed" },
    deactivated: { color: "#71717a", bg: "rgba(113,113,122,0.12)", label: "Deactivated" },
    reactivated: { color: "#3f7c59", bg: "rgba(63,124,89,0.10)", label: "Reactivated" },
  }[entry.action] || { color: "#71717a", bg: "rgba(113,113,122,0.12)", label: entry.action };

  const dateStr = entry.timestamp
    ? new Date(entry.timestamp).toLocaleString(undefined, {
        year: "numeric", month: "short", day: "numeric",
        hour: "2-digit", minute: "2-digit",
      })
    : "—";

  const summary = entry.action === "deactivated"
    ? `${entry.pct}% "${entry.label || "—"}" turned off`
    : `${entry.pct}% off · "${entry.label || "—"}"`;

  return (
    <li
      data-testid={`discount-audit-row-${entry.id}`}
      className="flex items-start gap-3 text-xs"
    >
      <span
        className="mt-0.5 shrink-0 inline-flex items-center px-2 py-0.5 rounded-full uppercase tracking-[0.1em] font-semibold text-[10px]"
        style={{ background: actionMeta.bg, color: actionMeta.color }}
      >
        {actionMeta.label}
      </span>
      <span className="flex-1 min-w-0">
        <span className="text-charcoal-600 font-medium block">{summary}</span>
        <span className="text-charcoal-400 block">
          {dateStr} · by {entry.admin_email || "admin"}
        </span>
      </span>
    </li>
  );
};

const Th = ({ children, className = "" }) => (
  <th className={`text-left text-[10px] uppercase tracking-[0.16em] font-medium px-4 py-3 ${className}`}>
    {children}
  </th>
);
const Td = ({ children, className = "" }) => (
  <td className={`px-4 py-3 align-middle ${className}`}>{children}</td>
);

// ---------- Inventory panel ----------
const InventoryPanel = ({ token }) => {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");
  const [acting, setActing] = useState("");

  const fetchRows = useCallback(async () => {
    try {
      setError("");
      const { data } = await axios.get(`${API}/admin/inventory`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRows(data || []);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load inventory");
    }
  }, [token]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const adjust = async (row, delta) => {
    await setStock(row, Math.max(0, (row.stock || 0) + delta));
  };

  const setStock = async (row, newStock) => {
    const key = `${row.product_id}-${row.variant_id}`;
    setActing(key);
    try {
      const { data } = await axios.patch(
        `${API}/admin/inventory/${row.product_id}/${row.variant_id}`,
        { stock: newStock },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setRows((prev) => prev.map((r) =>
        r.product_id === row.product_id && r.variant_id === row.variant_id
          ? { ...r, ...data } : r,
      ));
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not update stock");
    } finally {
      setActing("");
    }
  };

  if (error) {
    return <div className="surface-card p-8 text-sm text-red-700">{error}</div>;
  }
  if (rows === null) {
    return <div className="surface-card p-10 text-center text-charcoal-400 text-sm">Loading inventory…</div>;
  }

  const rowTone = (avail) =>
    avail === 0 ? "bg-red-50/70" : avail <= 5 ? "bg-amber-50/70" : "";

  return (
    <div className="space-y-4">
    <BulkStockPaste token={token} rows={rows} onApplied={fetchRows} />
    <div data-testid="admin-inventory-panel" className="surface-card overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm" data-testid="admin-inventory-table">
          <thead className="bg-cream-200/70 text-charcoal-500">
            <tr>
              <Th>Product</Th>
              <Th>Variant</Th>
              <Th className="text-right">Total Stock</Th>
              <Th className="text-right">Reserved</Th>
              <Th className="text-right">Available</Th>
              <Th className="text-right">Adjust</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const key = `${r.product_id}-${r.variant_id}`;
              const busy = acting === key;
              return (
                <tr
                  key={key}
                  data-testid={`inventory-row-${key}`}
                  className={`border-t border-charcoal-100/40 ${rowTone(r.available)} hover:bg-cream-200/50 transition-colors ${
                    r.available < 0 ? "bg-red-50" : ""
                  }`}
                >
                  <Td className="font-medium text-charcoal-600">
                    {r.product_name}
                    {r.available < 0 && (
                      <span
                        data-testid={`inv-drift-flag-${key}`}
                        className="ml-2 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-red-700 bg-red-100 border border-red-200 rounded-full px-2 py-0.5"
                        title="Reserved exceeds stock — investigate open orders for this SKU."
                      >
                        Drift
                      </span>
                    )}
                  </Td>
                  <Td className="text-charcoal-500">{r.variant_label}</Td>
                  <Td className="text-right font-medium">{r.stock}</Td>
                  <Td className={`text-right ${
                    r.reserved > r.stock ? "text-red-700 font-semibold" : "text-charcoal-400"
                  }`}>{r.reserved}</Td>
                  <Td className={`text-right font-display font-bold ${
                    r.available < 0 ? "text-red-700"
                    : r.available === 0 ? "text-red-700"
                    : r.available <= 5 ? "text-amber-700"
                    : "text-[#8B2252]"
                  }`}>
                    {r.available}
                  </Td>
                  <Td className="text-right">
                    <div className="inline-flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => adjust(r, -1)}
                        disabled={busy || r.stock <= 0}
                        data-testid={`inv-decr-${key}`}
                        className="w-7 h-7 rounded-full border border-charcoal-200 text-charcoal-500 hover:bg-cream-200 disabled:opacity-40"
                        aria-label="Decrease stock"
                      >−</button>
                      <StockInput row={r} busy={busy} testId={`inv-input-${key}`} onSave={(n) => setStock(r, n)} />
                      <button
                        type="button"
                        onClick={() => adjust(r, +1)}
                        disabled={busy}
                        data-testid={`inv-incr-${key}`}
                        className="w-7 h-7 rounded-full border border-charcoal-200 text-charcoal-500 hover:bg-cream-200 disabled:opacity-40"
                        aria-label="Increase stock"
                      >+</button>
                    </div>
                  </Td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
    </div>
  );
};

// Type an exact stock count; saves on Enter or when the field loses focus.
const StockInput = ({ row, busy, testId, onSave }) => {
  const [val, setVal] = useState(String(row.stock));
  useEffect(() => { setVal(String(row.stock)); }, [row.stock]);
  const commit = () => {
    const n = parseInt(val, 10);
    if (Number.isNaN(n) || n < 0) { setVal(String(row.stock)); return; }
    if (n !== row.stock) onSave(n);
  };
  return (
    <input
      type="number"
      min={0}
      inputMode="numeric"
      value={val}
      disabled={busy}
      onChange={(e) => setVal(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); if (e.key === "Escape") setVal(String(row.stock)); }}
      data-testid={testId}
      aria-label={`Stock for ${row.product_name} ${row.variant_label}`}
      className={`w-16 h-7 rounded-full border text-center text-xs font-medium tabular-nums focus:outline-none focus:ring-2 focus:ring-[#8B2252]/30 ${
        val !== String(row.stock) ? "border-[#8B2252] text-[#8B2252]" : "border-charcoal-200 text-charcoal-600"
      }`}
    />
  );
};
const CustomerOrdersDrawer = ({ token, customer, onClose }) => {
  const [orders, setOrders] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/admin/customers/${customer.id}/orders`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(({ data }) => { if (!cancelled) setOrders(data || []); })
      .catch((e) => { if (!cancelled) setError(e?.response?.data?.detail || "Failed to load"); });
    return () => { cancelled = true; };
  }, [token, customer.id]);

  return (
    <div
      data-testid="customer-orders-drawer"
      className="fixed inset-0 z-50 bg-charcoal-600/50 backdrop-blur-sm flex items-end md:items-center justify-end md:justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-3xl max-w-2xl w-full max-h-[85vh] overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 md:p-7 border-b border-charcoal-100">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="overline mb-1">Customer history</p>
              <h3 className="font-display font-bold text-2xl text-charcoal-600">
                {customer.first_name} {customer.last_name}
              </h3>
              <p className="text-sm text-charcoal-400 mt-0.5 break-all">{customer.email}</p>
              <p className="text-xs text-charcoal-400 mt-1">
                {customer.research_field} · Registered{" "}
                {customer.created_at ? new Date(customer.created_at).toLocaleDateString() : "—"}
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              data-testid="customer-orders-drawer-close"
              className="text-charcoal-400 hover:text-charcoal-600"
              aria-label="Close"
            >
              <XIcon className="w-5 h-5" />
            </button>
          </div>
          <div className="grid grid-cols-2 gap-4 mt-5">
            <div className="rounded-xl p-3" style={{ backgroundColor: "#FDFAF9" }}>
              <p className="text-[10px] uppercase tracking-[0.18em] text-charcoal-400">Orders</p>
              <p className="font-display font-bold text-2xl text-[#8B2252]">{customer.order_count}</p>
            </div>
            <div className="rounded-xl p-3" style={{ backgroundColor: "#FDFAF9" }}>
              <p className="text-[10px] uppercase tracking-[0.18em] text-charcoal-400">Lifetime spend</p>
              <p className="font-display font-bold text-2xl text-[#8B2252]">{usd(customer.total_spent)}</p>
            </div>
          </div>
        </div>

        <div className="p-6 md:p-7">
          {error && (
            <div className="p-3 rounded-2xl bg-red-50 text-red-700 text-sm">{error}</div>
          )}
          {orders === null && !error && (
            <p className="text-sm text-charcoal-400 text-center py-8">Loading…</p>
          )}
          {orders && orders.length === 0 && (
            <div className="text-center py-8">
              <Package className="w-7 h-7 text-charcoal-300 mx-auto mb-2" strokeWidth={1.5} />
              <p className="text-sm text-charcoal-400">No orders yet.</p>
            </div>
          )}
          {orders && orders.length > 0 && (
            <ul className="space-y-3" data-testid="customer-orders-drawer-list">
              {orders.map((o) => (
                <li
                  key={o.order_number}
                  className="rounded-2xl p-4 border border-charcoal-100/60 flex flex-wrap items-center justify-between gap-3"
                  style={{ backgroundColor: "#FDFAF9" }}
                >
                  <div>
                    <p className="font-display font-bold text-[#8B2252]">{o.order_number}</p>
                    <p className="text-xs text-charcoal-400">
                      {new Date(o.created_at).toLocaleDateString()} · {o.items.length} item{o.items.length === 1 ? "" : "s"}
                    </p>
                  </div>
                  <span className={`text-[10px] font-medium px-2.5 py-1 rounded-full uppercase tracking-wider ${statusPillClass(o.status)}`}>
                    {statusLabel(o.status)}
                  </span>
                  <span className="font-display font-bold text-charcoal-600">{usd(o.total)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

const NetRevenuePill = ({ order }) => {
  // Net revenue is server-computed (order_fee_and_net) — exact for paid Stripe
  // orders where we've fetched the balance_transaction, estimated formula
  // otherwise. Zelle orders show total (no processor fee).
  const net = typeof order.net_revenue === "number" ? order.net_revenue : null;
  if (net === null) return null;
  const fee = Math.max(0, (order.total || 0) - net);
  const isExact = !!order.net_revenue_is_exact;
  return (
    <span
      data-testid={`net-revenue-${order.order_number}`}
      className="mt-0.5 text-[11px] text-charcoal-400 font-medium"
      title={
        isExact
          ? "Actual Stripe fee from balance transaction"
          : "Estimated using 2.9%+30¢ (card) / 0.8% capped at $5 (ACH). Actual value populates once the webhook fires."
      }
    >
      Net ${net.toFixed(2)}
      {fee > 0 && (
        <span className="text-charcoal-300"> · fee ${fee.toFixed(2)}{isExact ? "" : "*"}</span>
      )}
    </span>
  );
};


const OrderCard = ({ order, acting, fulfilling, onConfirm, onStartFulfill, onCancelFulfill, onSubmitFulfill, onOpenBuyLabel, onToggleArchive, onDelete, onRestore, onPermanentDelete, onVoidManual, onEditItems, selectable = false, selected = false, onSelectToggle, trashMode = false }) => {
  const dateStr = new Date(order.created_at).toLocaleString(undefined, {
    year: "numeric", month: "short", day: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
  const isShipped = order.status === "Shipped / Fulfilled";
  const [showDetail, setShowDetail] = useState(false);
  // Trash-mode metadata: "Deleted X ago" + "Auto-purges in Y days".
  let deletedAgo = "";
  let purgesIn = "";
  if (trashMode && order.deleted_at) {
    const d = new Date(order.deleted_at);
    const ms = Date.now() - d.getTime();
    const hours = Math.floor(ms / 3_600_000);
    if (hours < 1) deletedAgo = "just now";
    else if (hours < 24) deletedAgo = `${hours}h ago`;
    else deletedAgo = `${Math.floor(hours / 24)}d ago`;
    const purgeMs = d.getTime() + 7 * 24 * 3_600_000 - Date.now();
    const purgeDays = Math.max(0, Math.ceil(purgeMs / (24 * 3_600_000)));
    purgesIn = purgeDays === 0 ? "any moment" : `${purgeDays}d`;
  }

  return (
    <article
      data-testid={`admin-order-${order.order_number}`}
      className={`surface-card p-6 md:p-7 transition-colors ${
        selected ? "ring-2 ring-[#8B2252]/50 bg-[#F5EAE6]/40" : ""
      }`}
    >
      <header className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-4">
        <div className="flex items-start gap-3">
          {selectable && (
            <input
              type="checkbox"
              data-testid={`admin-select-${order.order_number}`}
              checked={selected}
              onChange={onSelectToggle}
              className="w-5 h-5 mt-1 accent-[#8B2252] cursor-pointer flex-shrink-0"
              aria-label={`Select order ${order.order_number} for bulk delete`}
            />
          )}
          <div>
            <p className="font-display font-bold text-2xl text-[#8B2252]">{order.order_number}</p>
            <p className="text-xs text-charcoal-400 mt-0.5">{dateStr}</p>
            <button
              type="button"
              onClick={() => setShowDetail(true)}
              data-testid={`admin-view-details-${order.order_number}`}
              className="mt-1.5 text-xs font-medium text-[#8B2252] hover:underline underline-offset-2"
            >
              View full details
            </button>
            <OrderDetailModal order={order} open={showDetail} onOpenChange={setShowDetail} />
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <span
            data-testid={`admin-status-pill-${order.order_number}`}
            className={`text-[11px] font-medium px-3 py-1.5 rounded-full uppercase tracking-wider ${statusPillClass(order.status)}`}
          >
            {statusLabel(order.status)}
          </span>
          {order.shipping_method === "expedited" && (
            <span
              data-testid={`admin-expedited-badge-${order.order_number}`}
              className="text-[11px] font-medium px-3 py-1.5 rounded-full uppercase tracking-wider text-white inline-flex items-center gap-1"
              style={{ background: "#8B2252" }}
            >
              <Zap className="w-3 h-3" strokeWidth={2.5} />
              Expedited
            </span>
          )}
          {order.is_test_order && (
            <span
              data-testid={`admin-test-badge-${order.order_number}`}
              className="text-[11px] font-medium px-3 py-1.5 rounded-full uppercase tracking-wider bg-amber-100 text-amber-800 border border-amber-200"
              title="Stripe test-mode order — safe to delete"
            >
              Test
            </span>
          )}
          {order.source === "manual" && order.status !== "Refunded" && (
            <span
              data-testid={`admin-manual-badge-${order.order_number}`}
              className="text-[11px] font-medium px-3 py-1.5 rounded-full uppercase tracking-wider bg-[#F3E0EA] text-[#8B2252] border border-[#8B2252]/30"
              title={
                order.manual_payment_method
                  ? `Paid via ${order.manual_payment_method}${order.manual_payment_note ? ` — ${order.manual_payment_note}` : ""}`
                  : "Manually created (off-platform)"
              }
            >
              Manual · {order.manual_payment_method || "off-platform"}
            </span>
          )}
          {order.archived && (
            <span
              data-testid={`admin-archived-badge-${order.order_number}`}
              className="text-[11px] font-medium px-3 py-1.5 rounded-full uppercase tracking-wider bg-charcoal-100 text-charcoal-500"
            >
              Archived
            </span>
          )}
          {trashMode && order.deleted_at && (
            <span
              data-testid={`admin-deleted-pill-${order.order_number}`}
              className="text-[11px] font-medium px-3 py-1.5 rounded-full uppercase tracking-wider bg-red-100 text-red-700 border border-red-200"
              title={`Auto-purges in ${purgesIn}`}
            >
              Deleted {deletedAgo} · purges in {purgesIn}
            </span>
          )}
          <div className="flex flex-col items-end">
            <span
              data-testid={`admin-order-total-${order.order_number}`}
              className="font-display font-bold text-xl text-charcoal-600"
            >
              ${order.total.toFixed(2)}
            </span>
            {order.payment_method && (
              <NetRevenuePill order={order} />
            )}
          </div>
        </div>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 text-sm">
        <div>
          <p className="text-[10px] uppercase tracking-[0.18em] text-charcoal-300 font-medium mb-1.5">
            Customer
          </p>
          <p className="font-medium text-charcoal-600">{order.customer_name || "—"}</p>
          <p className="text-charcoal-400 break-all">{order.customer_email}</p>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-[0.18em] text-charcoal-300 font-medium mb-1.5">
            Shipping method
          </p>
          {order.shipping_method ? (
            <div className="text-charcoal-500 leading-relaxed">
              <p className="font-medium text-charcoal-600">
                {order.shipping_method_label || (order.shipping_method === "expedited" ? "Expedited" : "Standard")}
              </p>
              <p className="text-xs text-charcoal-400">
                {order.shipping_carrier_label || "USPS Priority Mail"}
              </p>
              <p className="text-xs text-charcoal-400">
                {order.shipping_estimated_days || "1-3 business days"}
              </p>
            </div>
          ) : (
            <p className="text-charcoal-400 italic">Not selected yet</p>
          )}
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-[0.18em] text-charcoal-300 font-medium mb-1.5">
            Shipping address
          </p>
          {order.shipping ? (
            <div className="text-charcoal-500 leading-relaxed">
              <p>{order.shipping.first_name} {order.shipping.last_name}</p>
              <p>{order.shipping.address1}</p>
              {order.shipping.address2 && <p>{order.shipping.address2}</p>}
              <p>
                {order.shipping.city}, {order.shipping.state} {order.shipping.zip_code}
              </p>
              <p className="text-xs text-charcoal-400">{order.shipping.phone}</p>
            </div>
          ) : (
            <p className="text-charcoal-400 italic">Awaiting customer to confirm payment + shipping</p>
          )}
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-[0.18em] text-charcoal-300 font-medium mb-1.5">
            Items
          </p>
          <ul className="space-y-1 text-charcoal-500">
            {order.items.map((it) => (
              <li key={`${it.product_id}-${it.variant_id}`} className="flex justify-between gap-3">
                <span>{it.product_name} · {it.variant_label} × {it.quantity}</span>
                <span className="text-charcoal-400">${it.line_total.toFixed(2)}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Existing tracking info (after fulfillment) */}
      {isShipped && order.carrier && order.tracking_number && (
        <div
          data-testid={`admin-tracking-info-${order.order_number}`}
          className="mt-5 pt-4 border-t border-charcoal-100/60 flex items-start gap-2 text-sm text-charcoal-500"
        >
          <Truck className="w-4 h-4 text-emerald-700 mt-0.5 shrink-0" strokeWidth={2} />
          <div>
            <p className="font-medium text-charcoal-600">
              {order.carrier} · <span className="select-all">{order.tracking_number}</span>
            </p>
            {order.tracking_url && (
              <a
                href={order.tracking_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center text-[#8B2252] hover:underline"
              >
                View tracking <ExternalLink className="w-3.5 h-3.5 ml-1" strokeWidth={2} />
              </a>
            )}
          </div>
        </div>
      )}

      {/* Action area */}
      <div className="mt-6 pt-5 border-t border-charcoal-100/60">
        {fulfilling ? (
          <FulfillForm
            acting={acting}
            onCancel={onCancelFulfill}
            onSubmit={onSubmitFulfill}
            orderNumber={order.order_number}
          />
        ) : (
          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={onConfirm}
              disabled={acting || order.status === "Payment Confirmed" || isShipped}
              data-testid={`admin-confirm-${order.order_number}`}
              className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <CheckCircle2 className="w-4 h-4 mr-2" strokeWidth={2} />
              Confirm Payment
            </button>

            {/* Primary fulfillment path: buy a label via EasyPost. */}
            {order.status === "Payment Confirmed" && (
              <button
                type="button"
                onClick={() => onOpenBuyLabel(order.order_number)}
                disabled={acting}
                data-testid={`admin-buy-label-${order.order_number}`}
                className="inline-flex items-center justify-center px-5 py-2.5 rounded-full font-medium text-white text-sm transition-all hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ background: "#8B2252" }}
              >
                <Printer className="w-4 h-4 mr-2" strokeWidth={2} />
                Buy & Print Label
              </button>
            )}

            {/* Reprint path: re-open the saved postage label PDF. */}
            {isShipped && order.easypost_postage_label_url && (
              <a
                href={order.easypost_postage_label_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`admin-reprint-label-${order.order_number}`}
                className="inline-flex items-center justify-center px-5 py-2.5 rounded-full font-medium text-white text-sm transition-all hover:opacity-90"
                style={{ background: "#8B2252" }}
              >
                <Printer className="w-4 h-4 mr-2" strokeWidth={2} />
                Reprint Label
              </a>
            )}

            {/* Legacy manual fulfillment for orders without a saved label
                (or before this feature shipped). */}
            {!isShipped && !order.easypost_postage_label_url && (
              <button
                type="button"
                onClick={onStartFulfill}
                disabled={acting || isShipped || order.status === "Pending Payment - Zelle"}
                data-testid={`admin-fulfill-${order.order_number}`}
                className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <PackageCheck className="w-4 h-4 mr-2" strokeWidth={2} />
                Mark Fulfilled (manual)
              </button>
            )}

            {/* Spacer pushes actions to the right end. */}
            <div className="grow" />

            {trashMode ? (
              <>
                <button
                  type="button"
                  onClick={onRestore}
                  disabled={acting}
                  data-testid={`admin-restore-${order.order_number}`}
                  className="inline-flex items-center text-sm px-4 py-2 rounded-full font-medium text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  style={{ backgroundColor: "#3f7c59" }}
                  title="Restore this order back to Archived"
                >
                  <ArchiveRestore className="w-4 h-4 mr-2" strokeWidth={2} />
                  Restore
                </button>
                <button
                  type="button"
                  onClick={onPermanentDelete}
                  disabled={acting}
                  data-testid={`admin-permanent-delete-${order.order_number}`}
                  className="inline-flex items-center text-sm px-4 py-2 rounded-full font-medium text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  style={{ backgroundColor: "#8B2252" }}
                  title="Skip the 7-day trash window and delete right now"
                >
                  <Trash2 className="w-4 h-4 mr-2" strokeWidth={2} />
                  Permanently delete
                </button>
              </>
            ) : (
              <>
                {/* Archive toggle — non-destructive, always available. */}
                <button
                  type="button"
                  onClick={() => onToggleArchive(!order.archived)}
                  disabled={acting}
                  data-testid={`admin-archive-${order.order_number}`}
                  className="inline-flex items-center text-sm px-4 py-2 rounded-full font-medium bg-white text-charcoal-500 border border-charcoal-100 hover:bg-cream-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  title={order.archived ? "Restore from archive" : "Archive (hide from default view)"}
                >
                  {order.archived ? (
                    <>
                      <ArchiveRestore className="w-4 h-4 mr-2" strokeWidth={2} />
                      Unarchive
                    </>
                  ) : (
                    <>
                      <Archive className="w-4 h-4 mr-2" strokeWidth={2} />
                      Archive
                    </>
                  )}
                </button>

                {/* Void / refund — only for MANUAL orders (cash / Venmo / Zelle
                    where no Stripe refund is possible) that aren't already
                    refunded. */}
                {order.source === "manual" && order.status !== "Refunded" && (
                  <button
                    type="button"
                    onClick={() => onVoidManual?.()}
                    disabled={acting}
                    data-testid={`admin-void-manual-${order.order_number}`}
                    className="inline-flex items-center text-sm px-4 py-2 rounded-full font-medium bg-white text-rose-700 border border-rose-200 hover:bg-rose-50 disabled:opacity-40 disabled:cursor-not-allowed"
                    title="Mark this manual order refunded (restores inventory)"
                  >
                    <Ban className="w-4 h-4 mr-2" strokeWidth={2} />
                    Void / refund
                  </button>
                )}

                {/* Edit line items — for correcting stale quantities. */}
                {(order.status === "Pending Payment - Zelle" ||
                  order.status === "Payment Confirmed") && (
                  <button
                    type="button"
                    onClick={() => onEditItems?.()}
                    disabled={acting}
                    data-testid={`admin-edit-items-${order.order_number}`}
                    className="inline-flex items-center text-sm px-4 py-2 rounded-full font-medium bg-white text-[#8B2252] border border-[#8B2252] hover:bg-[#8B2252]/10 disabled:opacity-40 disabled:cursor-not-allowed"
                    title="Correct line items and quantities"
                  >
                    <Pencil className="w-4 h-4 mr-2" strokeWidth={2} />
                    Edit items
                  </button>
                )}

                {/* Delete — soft-delete; moves the order to Recently Deleted. */}
                {(() => {
                  const isDeletable =
                    order.status === "Cancelled" ||
                    order.status === "Refunded" ||
                    order.status === "Partially Refunded" ||
                    !!order.is_test_order ||
                    !!order.archived;
                  return (
                    <button
                      type="button"
                      onClick={onDelete}
                      disabled={acting || !isDeletable}
                      data-testid={`admin-delete-${order.order_number}`}
                      className="inline-flex items-center text-sm px-4 py-2 rounded-full font-medium bg-white text-red-700 border border-red-200 hover:bg-red-50 disabled:opacity-40 disabled:cursor-not-allowed"
                      title={
                        isDeletable
                          ? "Move to Recently Deleted (auto-purged after 7 days)"
                          : "Archive the order, or issue a refund, before deleting."
                      }
                    >
                      <Trash2 className="w-4 h-4 mr-2" strokeWidth={2} />
                      Delete
                    </button>
                  );
                })()}
              </>
            )}
          </div>
        )}
      </div>
    </article>
  );
};

const FulfillForm = ({ acting, onCancel, onSubmit, orderNumber }) => {
  const [carrier, setCarrier] = useState("");
  const [tracking, setTracking] = useState("");
  const [localError, setLocalError] = useState("");

  const handleSubmit = () => {
    setLocalError("");
    if (!carrier) {
      setLocalError("Please select a carrier.");
      return;
    }
    if (!tracking.trim()) {
      setLocalError("Please enter a tracking number.");
      return;
    }
    onSubmit(carrier, tracking.trim());
  };

  return (
    <div
      data-testid={`admin-fulfill-form-${orderNumber}`}
      className="bg-cream-200/60 rounded-2xl p-5"
    >
      <div className="flex items-center justify-between mb-4">
        <p className="overline">Add tracking · {orderNumber}</p>
        <button
          type="button"
          onClick={onCancel}
          data-testid={`admin-fulfill-cancel-${orderNumber}`}
          className="text-charcoal-400 hover:text-charcoal-600"
          aria-label="Cancel"
        >
          <XIcon className="w-4 h-4" />
        </button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <label className="block">
          <span className="block text-xs font-medium uppercase tracking-[0.18em] text-charcoal-400 mb-2">
            Carrier
          </span>
          <div className="relative">
            <select
              data-testid={`admin-fulfill-carrier-${orderNumber}`}
              value={carrier}
              onChange={(e) => setCarrier(e.target.value)}
              className="input appearance-none pr-10"
            >
              <option value="" disabled>Select carrier…</option>
              {CARRIER_OPTIONS.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
            <ChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-charcoal-400" />
          </div>
        </label>
        <label className="block">
          <span className="block text-xs font-medium uppercase tracking-[0.18em] text-charcoal-400 mb-2">
            Tracking number
          </span>
          <input
            type="text"
            data-testid={`admin-fulfill-tracking-${orderNumber}`}
            value={tracking}
            onChange={(e) => setTracking(e.target.value)}
            className="input"
            placeholder="e.g. 9400111899223344556677"
          />
        </label>
      </div>
      {localError && (
        <p
          data-testid={`admin-fulfill-error-${orderNumber}`}
          className="mt-3 text-sm text-red-700"
        >
          {localError}
        </p>
      )}
      <div className="mt-5 flex flex-wrap gap-3">
        <button
          type="button"
          onClick={handleSubmit}
          disabled={acting}
          data-testid={`admin-fulfill-submit-${orderNumber}`}
          className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
        >
          <Truck className="w-4 h-4 mr-2" strokeWidth={2} />
          {acting ? "Saving…" : "Save & Mark Shipped"}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={acting}
          className="btn-secondary"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};


// ---------- Discounts panel ----------
const DiscountsPanel = ({ token }) => {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");
  const [acting, setActing] = useState("");
  const [editing, setEditing] = useState(null);   // null | "new" | rowObj
  const [usageFor, setUsageFor] = useState(null);

  const fetchRows = useCallback(async () => {
    try {
      setError("");
      const { data } = await axios.get(`${API}/admin/discounts`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRows(data || []);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load discounts");
    }
  }, [token]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const toggleActive = async (code, active) => {
    setActing(code);
    try {
      const { data } = await axios.patch(
        `${API}/admin/discounts/${code}`,
        { active: !active },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setRows((prev) => prev.map((r) => (r.code === code ? data : r)));
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not update code");
    } finally {
      setActing("");
    }
  };

  const remove = async (code) => {
    if (!window.confirm(`Delete discount code ${code}? Redemption history will be kept for accounting. This cannot be undone.`)) return;
    setActing(code);
    try {
      await axios.delete(`${API}/admin/discounts/${code}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRows((prev) => prev.filter((r) => r.code !== code));
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not delete code");
    } finally {
      setActing("");
    }
  };

  return (
    <div data-testid="admin-discounts-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <p className="text-sm text-charcoal-400 max-w-xl">
          Create percentage or fixed-dollar coupon codes researchers can apply at
          checkout. Coupons stack with the built-in FIRST10 welcome promo, volume
          discounts, and per-customer Friends &amp; Family discounts.
        </p>
        <button
          type="button"
          onClick={() => setEditing("new")}
          data-testid="admin-discount-new"
          className="btn-primary"
        >
          <Plus className="w-4 h-4 mr-2" strokeWidth={2} />
          New coupon
        </button>
      </div>

      {error && (
        <div data-testid="admin-discounts-error" className="mb-4 p-3 rounded-2xl bg-red-50 text-red-700 text-sm">
          {error}
        </div>
      )}

      {rows === null ? (
        <div className="surface-card p-10 text-center text-charcoal-400 text-sm">Loading coupons…</div>
      ) : rows.length === 0 ? (
        <div data-testid="admin-discounts-empty" className="surface-card p-10 text-center">
          <Tags className="w-7 h-7 text-charcoal-300 mx-auto mb-3" strokeWidth={1.5} />
          <p className="font-display text-lg text-charcoal-600 mb-1">No coupon codes yet.</p>
          <p className="text-sm text-charcoal-400">Click &quot;New coupon&quot; to create your first promo.</p>
        </div>
      ) : (
        <div className="surface-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm" data-testid="admin-discounts-table">
              <thead className="bg-cream-200/70 text-charcoal-500">
                <tr>
                  <Th>Code</Th>
                  <Th className="text-right">Discount</Th>
                  <Th>Internal note</Th>
                  <Th>Expires</Th>
                  <Th className="text-right">Uses</Th>
                  <Th className="text-right">Given out</Th>
                  <Th>Status</Th>
                  <Th className="text-right">Actions</Th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const busy = acting === r.code;
                  const statusTone = !r.active ? "bg-charcoal-100 text-charcoal-500"
                    : r.expired ? "bg-red-100 text-red-700"
                    : "bg-sage-100 text-sage-700";
                  const statusLabel = !r.active ? "Inactive"
                    : r.expired ? "Expired" : "Active";
                  const discLabel = r.amount_off
                    ? `$${Number(r.amount_off).toFixed(2)} off`
                    : `${r.percent}% off`;
                  return (
                    <tr
                      key={r.code}
                      data-testid={`discount-row-${r.code}`}
                      className="border-t border-charcoal-100/40 hover:bg-cream-200/50 transition-colors"
                    >
                      <Td className="font-display font-bold text-[#8B2252]">
                        <div>{r.code}</div>
                        {r.one_per_customer && (
                          <span
                            data-testid={`discount-one-per-badge-${r.code}`}
                            className="inline-block mt-0.5 text-[9px] font-medium uppercase tracking-wider bg-plum-50 text-charcoal-500 px-1.5 py-0.5 rounded-full"
                            style={{ background: "rgba(139,34,82,0.08)", color: "#8B2252" }}
                            title="1 use per customer"
                          >
                            1 / customer
                          </span>
                        )}
                        {r.description && (
                          <div className="text-[11px] text-charcoal-400 mt-0.5 font-sans font-normal normal-case">
                            {r.description}
                          </div>
                        )}
                      </Td>
                      <Td className="text-right font-medium text-charcoal-600">
                        {discLabel}
                      </Td>
                      <Td className="text-charcoal-500 max-w-xs">
                        <span className="text-xs">
                          {r.internal_note || <span className="text-charcoal-300">—</span>}
                        </span>
                      </Td>
                      <Td className="text-charcoal-400">
                        {r.expires_at ? new Date(r.expires_at).toLocaleDateString() : "Never"}
                      </Td>
                      <Td className="text-right text-charcoal-500">
                        {r.times_used}{r.max_uses ? ` / ${r.max_uses}` : ""}
                      </Td>
                      <Td className="text-right text-charcoal-500 font-medium">
                        {r.total_discount_given > 0
                          ? `$${Number(r.total_discount_given).toFixed(2)}`
                          : <span className="text-charcoal-300">—</span>}
                      </Td>
                      <Td>
                        <span className={`text-[10px] font-medium px-2.5 py-1 rounded-full uppercase tracking-wider ${statusTone}`}>
                          {statusLabel}
                        </span>
                      </Td>
                      <Td className="text-right">
                        <div className="inline-flex items-center gap-3">
                          <button
                            type="button"
                            onClick={() => setUsageFor(r)}
                            data-testid={`discount-usage-${r.code}`}
                            className="text-xs font-medium text-charcoal-500 hover:text-[#8B2252]"
                          >
                            Usage
                          </button>
                          <button
                            type="button"
                            onClick={() => setEditing(r)}
                            data-testid={`discount-edit-${r.code}`}
                            className="text-xs font-medium text-charcoal-500 hover:text-[#8B2252]"
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => toggleActive(r.code, r.active)}
                            disabled={busy}
                            data-testid={`discount-toggle-${r.code}`}
                            className="text-xs font-medium text-charcoal-500 hover:text-[#8B2252] disabled:opacity-40"
                          >
                            {r.active ? "Disable" : "Enable"}
                          </button>
                          <button
                            type="button"
                            onClick={() => remove(r.code)}
                            disabled={busy}
                            data-testid={`discount-delete-${r.code}`}
                            className="text-xs font-medium text-red-700 hover:text-red-900 disabled:opacity-40 inline-flex items-center"
                            aria-label={`Delete ${r.code}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {editing && (
        <DiscountEditorDrawer
          token={token}
          initial={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={(d) => {
            setRows((prev) => {
              const list = prev || [];
              const idx = list.findIndex((r) => r.code === d.code);
              if (idx >= 0) {
                const next = [...list]; next[idx] = d;
                return next;
              }
              return [d, ...list];
            });
            setEditing(null);
          }}
        />
      )}

      {usageFor && (
        <CouponUsageModal
          token={token}
          code={usageFor.code}
          onClose={() => setUsageFor(null)}
        />
      )}

      <RedemptionsByUserPanel token={token} />
    </div>
  );
};

// Cross-code redemption ledger — surfaces which customers have used which
// codes and how many times. Useful for spotting coupon-abuse patterns
// (e.g. the FIRST10 stacking bug that motivated this feature). Backed by
// GET /api/admin/discounts/redemptions/by-user which aggregates the
// discount_redemptions collection.
const RedemptionsByUserPanel = ({ token }) => {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("");
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/admin/discounts/redemptions/by-user`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(({ data }) => { if (!cancelled) setRows(data?.rows || []); })
      .catch((e) => {
        if (!cancelled) setError(e?.response?.data?.detail || "Failed to load redemptions");
      });
    return () => { cancelled = true; };
  }, [token]);
  if (rows === null && !error) {
    return <p className="text-sm text-charcoal-400 mt-8">Loading redemptions…</p>;
  }
  const q = filter.trim().toLowerCase();
  const visible = (rows || []).filter((r) =>
    !q
    || (r.code || "").toLowerCase().includes(q)
    || (r.customer_email || "").toLowerCase().includes(q),
  );
  return (
    <section className="mt-10" data-testid="admin-redemptions-by-user">
      <div className="flex items-end justify-between gap-3 mb-3">
        <div>
          <p className="overline text-[#8B2252]">Abuse watch</p>
          <h3 className="font-display text-xl text-charcoal-800 mt-1">Redemptions by user</h3>
          <p className="text-xs text-charcoal-500 mt-1 max-w-lg">
            Every (customer, coupon) pair with how many times they've
            redeemed it. Sorted by uses so heavy redeemers surface first.
          </p>
        </div>
        <input
          data-testid="admin-redemptions-filter"
          placeholder="Filter by code or email"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="input max-w-xs text-sm"
        />
      </div>
      {error && (
        <div className="p-3 rounded-2xl bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>
      )}
      {visible.length === 0 ? (
        <p className="text-sm text-charcoal-400">No redemptions to show.</p>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-charcoal-100 bg-white">
          <table className="min-w-full text-sm">
            <thead className="bg-cream-100 text-charcoal-500 text-[11px] uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-2">Customer</th>
                <th className="text-left px-4 py-2">Code</th>
                <th className="text-right px-4 py-2">Uses</th>
                <th className="text-right px-4 py-2">Total discount</th>
                <th className="text-left px-4 py-2">Last used</th>
                <th className="text-left px-4 py-2">Source</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((r, i) => {
                const abuse = r.uses > 1;
                const last = r.last_used
                  ? new Date(r.last_used).toLocaleString(undefined, {
                      month: "short", day: "numeric", year: "numeric",
                      hour: "numeric", minute: "2-digit",
                    })
                  : "—";
                return (
                  <tr key={i} className={abuse ? "bg-amber-50" : ""}
                      data-testid={`admin-redemption-row-${i}`}>
                    <td className="px-4 py-2 text-charcoal-700">{r.customer_email || <span className="text-charcoal-400">unknown</span>}</td>
                    <td className="px-4 py-2 font-mono text-xs">{r.code}</td>
                    <td className={`px-4 py-2 text-right ${abuse ? "text-amber-800 font-semibold" : "text-charcoal-700"}`}>{r.uses}</td>
                    <td className="px-4 py-2 text-right text-charcoal-700">${Number(r.total_discount).toFixed(2)}</td>
                    <td className="px-4 py-2 text-charcoal-500">{last}</td>
                    <td className="px-4 py-2 text-charcoal-400 text-xs">{(r.sources || []).join(", ") || "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};

// Coupon create/edit drawer. When `initial` is null we're creating; when it's
// a row object we're editing that coupon. Handles both % and $ types with a
// radio switch, plus one-per-customer, expiry, max_uses, and internal note.
const DiscountEditorDrawer = ({ token, initial, onClose, onSaved }) => {
  const isEdit = !!initial;
  const [form, setForm] = useState({
    code: initial?.code || "",
    type: initial?.amount_off ? "amount" : "percent",
    percent: String(initial?.percent ?? 10),
    amount_off: String(initial?.amount_off ?? ""),
    description: initial?.description || "",
    internal_note: initial?.internal_note || "",
    expires_at: initial?.expires_at
      ? initial.expires_at.slice(0, 10)   // ISO → YYYY-MM-DD for <input type=date>
      : "",
    max_uses: initial?.max_uses ? String(initial.max_uses) : "",
    one_per_customer: !!initial?.one_per_customer,
    active: initial ? !!initial.active : true,
  });
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const set = (key) => (e) =>
    setForm((p) => ({
      ...p,
      [key]: e.target.type === "checkbox" ? e.target.checked : e.target.value,
    }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    const code = form.code.trim().toUpperCase();
    if (!isEdit && (!code || !/^[A-Za-z0-9]+$/.test(code))) {
      setError("Code must be alphanumeric (letters & digits only).");
      return;
    }
    const payload = {
      description: form.description.trim() || null,
      internal_note: form.internal_note.trim() || null,
      expires_at: form.expires_at || (isEdit ? "" : null),
      max_uses: form.max_uses ? Number(form.max_uses) : (isEdit ? 0 : null),
      one_per_customer: form.one_per_customer,
      active: form.active,
    };
    if (form.type === "percent") {
      const pct = Number(form.percent);
      if (!pct || pct < 1 || pct > 90) {
        setError("Percent must be between 1 and 90.");
        return;
      }
      payload.percent = pct;
      if (isEdit) payload.amount_off = 0;   // clear the $ side when switching
    } else {
      const amt = Number(form.amount_off);
      if (!(amt > 0) || amt > 100000) {
        setError("Dollar amount must be greater than 0.");
        return;
      }
      payload.amount_off = amt;
    }

    setSubmitting(true);
    try {
      if (isEdit) {
        const { data } = await axios.patch(
          `${API}/admin/discounts/${initial.code}`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        onSaved(data);
      } else {
        const { data } = await axios.post(
          `${API}/admin/discounts`,
          { code, ...payload },
          { headers: { Authorization: `Bearer ${token}` } },
        );
        onSaved(data);
      }
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not save coupon");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="discount-editor-drawer"
      className="fixed inset-0 z-[70] bg-charcoal-600/50 backdrop-blur-sm flex items-end md:items-center justify-end md:justify-center p-4"
      onClick={onClose}
    >
      <form
        onSubmit={submit}
        className="bg-white rounded-3xl max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-2xl p-6 md:p-7"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-5">
          <div>
            <p className="overline mb-1">
              {isEdit ? "Edit coupon" : "New coupon code"}
            </p>
            <h3 className="font-display font-bold text-2xl text-charcoal-600">
              {isEdit ? initial.code : "Create a coupon"}
            </h3>
          </div>
          <button
            type="button" onClick={onClose}
            data-testid="discount-editor-close"
            className="text-charcoal-400 hover:text-charcoal-600"
          >
            <XIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {!isEdit && (
            <label className="block sm:col-span-2">
              <span className="block text-xs font-medium uppercase tracking-[0.18em] text-charcoal-400 mb-2">Code</span>
              <input
                type="text" required
                value={form.code}
                onChange={(e) => setForm((p) => ({ ...p, code: e.target.value.toUpperCase() }))}
                placeholder="e.g. AFFILIATE10"
                data-testid="discount-editor-code"
                className="input uppercase tracking-wider"
              />
            </label>
          )}

          {/* Type radio */}
          <div className="block sm:col-span-2">
            <span className="block text-xs font-medium uppercase tracking-[0.18em] text-charcoal-400 mb-2">
              Discount type
            </span>
            <div className="grid grid-cols-2 gap-3">
              {[
                { key: "percent", label: "Percentage off" },
                { key: "amount",  label: "Fixed $ off" },
              ].map((opt) => {
                const on = form.type === opt.key;
                return (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => setForm((p) => ({ ...p, type: opt.key }))}
                    data-testid={`discount-type-${opt.key}`}
                    className={`px-4 py-3 rounded-2xl border-2 text-sm font-medium text-left transition-colors ${
                      on
                        ? "border-[#8B2252] bg-[#8B2252]/[0.04] text-charcoal-600"
                        : "border-charcoal-100 bg-cream-100 text-charcoal-500 hover:bg-cream-200/40"
                    }`}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>

          {form.type === "percent" ? (
            <label className="block">
              <span className="block text-xs font-medium uppercase tracking-[0.18em] text-charcoal-400 mb-2">Percent off</span>
              <div className="relative">
                <input
                  type="number" required min="1" max="90"
                  value={form.percent} onChange={set("percent")}
                  data-testid="discount-editor-percent"
                  className="input pr-10"
                />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-charcoal-400">%</span>
              </div>
            </label>
          ) : (
            <label className="block">
              <span className="block text-xs font-medium uppercase tracking-[0.18em] text-charcoal-400 mb-2">Dollars off</span>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-charcoal-400">$</span>
                <input
                  type="number" required min="0.01" step="0.01"
                  value={form.amount_off} onChange={set("amount_off")}
                  placeholder="e.g. 15"
                  data-testid="discount-editor-amount-off"
                  className="input pl-8"
                />
              </div>
            </label>
          )}

          <label className="block">
            <span className="block text-xs font-medium uppercase tracking-[0.18em] text-charcoal-400 mb-2">Max uses (optional)</span>
            <input
              type="number" min="1"
              value={form.max_uses} onChange={set("max_uses")}
              placeholder="Unlimited"
              data-testid="discount-editor-max-uses"
              className="input"
            />
          </label>

          <label className="block sm:col-span-2">
            <span className="block text-xs font-medium uppercase tracking-[0.18em] text-charcoal-400 mb-2">Description (customer-facing, optional)</span>
            <input
              type="text" value={form.description} onChange={set("description")}
              placeholder="Shown to customers at checkout"
              data-testid="discount-editor-description"
              className="input"
              maxLength={200}
            />
          </label>

          <label className="block sm:col-span-2">
            <span className="block text-xs font-medium uppercase tracking-[0.18em] text-charcoal-400 mb-2">Internal note (admin only)</span>
            <input
              type="text" value={form.internal_note} onChange={set("internal_note")}
              placeholder="e.g. John's affiliate code, Instagram promo July"
              data-testid="discount-editor-internal-note"
              className="input"
              maxLength={300}
            />
            <p className="text-[11px] text-charcoal-400 mt-1">
              Never shown to customers. Use this to remember who or what the code is for.
            </p>
          </label>

          <label className="block sm:col-span-2">
            <span className="block text-xs font-medium uppercase tracking-[0.18em] text-charcoal-400 mb-2">Expires (optional)</span>
            <input
              type="date" value={form.expires_at} onChange={set("expires_at")}
              data-testid="discount-editor-expires"
              className="input"
            />
          </label>

          <label className="flex items-center gap-2 sm:col-span-2">
            <input
              type="checkbox" checked={form.one_per_customer} onChange={set("one_per_customer")}
              data-testid="discount-editor-one-per-customer"
              className="w-4 h-4 accent-[#8B2252]"
            />
            <span className="text-sm text-charcoal-500">
              Limit to 1 use per customer
            </span>
          </label>

          <label className="flex items-center gap-2 sm:col-span-2">
            <input
              type="checkbox" checked={form.active} onChange={set("active")}
              data-testid="discount-editor-active"
              className="w-4 h-4 accent-[#8B2252]"
            />
            <span className="text-sm text-charcoal-500">
              {isEdit ? "Active" : "Active immediately"}
            </span>
          </label>
        </div>

        {error && (
          <p data-testid="discount-editor-error" className="mt-4 text-sm text-red-700">
            {error}
          </p>
        )}

        <div className="mt-6 flex flex-wrap gap-3">
          <button
            type="submit"
            disabled={submitting}
            data-testid="discount-editor-submit"
            className="btn-primary disabled:opacity-60"
          >
            {submitting ? "Saving…" : isEdit ? "Save changes" : "Create coupon"}
          </button>
          <button type="button" onClick={onClose} className="btn-secondary">
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

// Redemption ledger modal — shows every order that used the coupon, who
// used it, and the discount amount given out.
const CouponUsageModal = ({ token, code, onClose }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    (async () => {
      try {
        setError("");
        const { data } = await axios.get(
          `${API}/admin/discounts/${code}/usage`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        setData(data);
      } catch (e) {
        setError(e?.response?.data?.detail || "Failed to load usage");
      }
    })();
  }, [code, token]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="coupon-usage-modal"
      className="fixed inset-0 z-[70] flex items-center justify-center px-4 py-8"
    >
      <div className="absolute inset-0 bg-charcoal-600/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-2xl bg-white rounded-3xl shadow-2xl max-h-[85vh] overflow-y-auto p-6 md:p-7">
        <button
          type="button" onClick={onClose}
          data-testid="coupon-usage-close"
          className="absolute top-4 right-4 text-charcoal-400 hover:text-charcoal-600"
          aria-label="Close"
        >
          <XIcon className="w-5 h-5" strokeWidth={2} />
        </button>
        <p className="overline mb-1">Coupon usage</p>
        <h3 className="font-display font-bold text-2xl text-[#8B2252]">{code}</h3>
        {error && (
          <p data-testid="coupon-usage-error" className="mt-4 text-sm text-red-700">
            {error}
          </p>
        )}
        {!data ? (
          <p className="mt-6 text-sm text-charcoal-400">Loading…</p>
        ) : (
          <>
            <div className="mt-4 grid grid-cols-3 gap-3">
              <CouponStat label="Total uses" value={String(data.redemptions.length)} />
              <CouponStat label="Unique customers" value={String(data.unique_customers)} />
              <CouponStat
                label="Given out"
                value={`$${Number(data.total_discount_given || 0).toFixed(2)}`}
              />
            </div>
            {data.redemptions.length === 0 ? (
              <div
                data-testid="coupon-usage-empty"
                className="mt-6 rounded-2xl border border-charcoal-100 bg-cream-100 p-6 text-center text-sm text-charcoal-400"
              >
                This coupon has not been used yet.
              </div>
            ) : (
              <div className="mt-6 overflow-x-auto">
                <table
                  data-testid="coupon-usage-table"
                  className="min-w-full text-sm"
                >
                  <thead className="bg-cream-200/70 text-charcoal-500">
                    <tr>
                      <Th>Order</Th>
                      <Th>Customer</Th>
                      <Th className="text-right">Discount</Th>
                      <Th>When</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.redemptions.map((r) => (
                      <tr
                        key={`${r.order_number}-${r.timestamp}`}
                        data-testid={`coupon-usage-row-${r.order_number}`}
                        className="border-t border-charcoal-100/40"
                      >
                        <Td className="font-medium text-charcoal-600">
                          {r.order_number}
                        </Td>
                        <Td className="text-charcoal-500 break-all">
                          {r.customer_email}
                        </Td>
                        <Td className="text-right font-medium text-[#8B2252]">
                          ${Number(r.discount_amount).toFixed(2)}
                        </Td>
                        <Td className="text-charcoal-400 whitespace-nowrap">
                          {r.timestamp
                            ? new Date(r.timestamp).toLocaleString(undefined, {
                                year: "numeric", month: "short", day: "numeric",
                                hour: "2-digit", minute: "2-digit",
                              })
                            : "—"}
                        </Td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// Small stat card used inside CouponUsageModal.
const CouponStat = ({ label, value }) => (
  <div className="rounded-2xl bg-cream-100 border border-charcoal-100/50 p-3">
    <p className="text-[10px] font-medium uppercase tracking-[0.14em] text-charcoal-400">
      {label}
    </p>
    <p className="mt-1 font-display font-bold text-xl text-charcoal-600">
      {value}
    </p>
  </div>
);

// ---------- Promo Slides admin panel ----------
const PromoSlidesPanel = ({ token }) => {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null);   // null | "new" | rowObj

  const fetchRows = useCallback(async () => {
    try {
      setError("");
      const { data } = await axios.get(`${API}/admin/promo-slides`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRows(data || []);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load slides");
    }
  }, [token]);
  useEffect(() => { fetchRows(); }, [fetchRows]);

  const toggle = async (row) => {
    try {
      const { data } = await axios.patch(
        `${API}/admin/promo-slides/${row.id}`,
        { enabled: !row.enabled },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setRows((prev) => prev.map((r) => (r.id === row.id ? data : r)));
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not toggle");
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete slide "${row.headline}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/promo-slides/${row.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRows((prev) => prev.filter((r) => r.id !== row.id));
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not delete");
    }
  };

  const reorder = async (row, delta) => {
    try {
      const { data } = await axios.patch(
        `${API}/admin/promo-slides/${row.id}`,
        { order: Math.max(0, row.order + delta) },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setRows((prev) =>
        [...prev.map((r) => (r.id === row.id ? data : r))]
          .sort((a, b) => a.order - b.order),
      );
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not reorder");
    }
  };

  return (
    <div data-testid="admin-promo-slides-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <p className="text-sm text-charcoal-400 max-w-xl">
          Manage the rotating promo banner on the Compounds page. Enabled +
          in-date-range slides show automatically, sorted by order.
        </p>
        <button
          type="button"
          onClick={() => setEditing("new")}
          data-testid="admin-promo-slide-new"
          className="btn-primary"
        >
          <Plus className="w-4 h-4 mr-2" strokeWidth={2} />
          New slide
        </button>
      </div>

      {error && (
        <div data-testid="admin-promo-slides-error" className="mb-4 p-3 rounded-2xl bg-red-50 text-red-700 text-sm">
          {error}
        </div>
      )}

      {rows === null ? (
        <div className="surface-card p-10 text-center text-charcoal-400 text-sm">Loading slides…</div>
      ) : rows.length === 0 ? (
        <div className="surface-card p-10 text-center">
          <Sparkles className="w-7 h-7 text-charcoal-300 mx-auto mb-3" strokeWidth={1.5} />
          <p className="font-display text-lg text-charcoal-600 mb-1">No slides yet.</p>
          <p className="text-sm text-charcoal-400">Click &quot;New slide&quot; to add one.</p>
        </div>
      ) : (
        <div className="surface-card overflow-hidden">
          <table className="min-w-full text-sm" data-testid="admin-promo-slides-table">
            <thead className="bg-cream-200/70 text-charcoal-500">
              <tr>
                <Th className="text-right">Order</Th>
                <Th>Type</Th>
                <Th>Eyebrow / Headline</Th>
                <Th>Dates</Th>
                <Th>Enabled</Th>
                <Th className="text-right">Actions</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, idx) => (
                <tr
                  key={r.id}
                  data-testid={`promo-slide-row-${r.id}`}
                  className="border-t border-charcoal-100/40"
                >
                  <Td className="text-right">
                    <div className="inline-flex items-center gap-1">
                      <span className="font-mono text-charcoal-500">{r.order}</span>
                      <button
                        type="button" onClick={() => reorder(r, -5)}
                        disabled={idx === 0}
                        data-testid={`promo-slide-up-${r.id}`}
                        className="text-charcoal-400 hover:text-charcoal-700 disabled:opacity-30 text-lg leading-none px-1"
                        aria-label="Move up"
                      >↑</button>
                      <button
                        type="button" onClick={() => reorder(r, 5)}
                        disabled={idx === rows.length - 1}
                        data-testid={`promo-slide-down-${r.id}`}
                        className="text-charcoal-400 hover:text-charcoal-700 disabled:opacity-30 text-lg leading-none px-1"
                        aria-label="Move down"
                      >↓</button>
                    </div>
                  </Td>
                  <Td>
                    <span className="text-[10px] font-medium uppercase tracking-wider px-2 py-1 rounded-full"
                          style={{
                            background: r.type === "email_capture"
                              ? "rgba(63,124,89,0.10)" : "rgba(139,34,82,0.10)",
                            color: r.type === "email_capture"
                              ? "#3f7c59" : "#8B2252",
                          }}>
                      {r.type === "email_capture" ? "Email" : "Promo"}
                    </span>
                  </Td>
                  <Td>
                    <div className="text-[11px] text-charcoal-400 uppercase tracking-wider">
                      {r.eyebrow_label || "—"}
                    </div>
                    <div className="font-medium text-charcoal-600">{r.headline}</div>
                  </Td>
                  <Td className="text-charcoal-400 text-xs">
                    {r.start_date || "always"} → {r.end_date || "no end"}
                  </Td>
                  <Td>
                    <button
                      type="button"
                      onClick={() => toggle(r)}
                      data-testid={`promo-slide-toggle-${r.id}`}
                      className={`relative inline-flex h-5 w-9 rounded-full transition-colors ${
                        r.enabled ? "bg-sage-600" : "bg-charcoal-200"
                      }`}
                      aria-label={r.enabled ? "Disable" : "Enable"}
                    >
                      <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform ${
                        r.enabled ? "translate-x-4" : "translate-x-0.5"
                      }`} />
                    </button>
                  </Td>
                  <Td className="text-right">
                    <button
                      type="button" onClick={() => setEditing(r)}
                      data-testid={`promo-slide-edit-${r.id}`}
                      className="text-xs font-medium text-charcoal-500 hover:text-[#8B2252] mr-3"
                    >
                      Edit
                    </button>
                    <button
                      type="button" onClick={() => remove(r)}
                      data-testid={`promo-slide-delete-${r.id}`}
                      className="text-xs font-medium text-red-700 hover:text-red-900"
                      aria-label="Delete"
                    >
                      <Trash2 className="w-3.5 h-3.5 inline" />
                    </button>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <PromoSlideEditor
          token={token}
          initial={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={(d) => {
            setRows((prev) => {
              const list = prev || [];
              const idx = list.findIndex((r) => r.id === d.id);
              const next = idx >= 0
                ? [...list.slice(0, idx), d, ...list.slice(idx + 1)]
                : [...list, d];
              return next.sort((a, b) => a.order - b.order);
            });
            setEditing(null);
          }}
        />
      )}
    </div>
  );
};

const PromoSlideEditor = ({ token, initial, onClose, onSaved }) => {
  const isEdit = !!initial;
  const [f, setF] = useState({
    order: initial?.order ?? 100,
    enabled: initial ? !!initial.enabled : true,
    type: initial?.type || "promo_code",
    headline: initial?.headline || "",
    subtext: initial?.subtext || "",
    eyebrow_label: initial?.eyebrow_label || "",
    accent_color: initial?.accent_color || "#8B2252",
    icon: initial?.icon || "Sparkles",
    start_date: initial?.start_date || "",
    end_date: initial?.end_date || "",
    promo_code: initial?.promo_code || "",
    discount_type: initial?.discount_type || "percent",
    discount_amount: initial?.discount_amount ?? "",
    messages_a: (initial?.messages || [])[0] || "",
    messages_b: (initial?.messages || [])[1] || "",
    cta_label: initial?.cta_label || "Claim",
    coupon_code_on_signup: initial?.coupon_code_on_signup || "FIRST10",
  });
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) =>
    setF((p) => ({ ...p, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    const body = {
      order: Number(f.order) || 0,
      enabled: f.enabled,
      type: f.type,
      headline: f.headline.trim(),
      subtext: f.subtext.trim() || null,
      eyebrow_label: f.eyebrow_label.trim() || null,
      accent_color: f.accent_color || null,
      icon: f.icon,
      start_date: f.start_date || null,
      end_date: f.end_date || null,
    };
    if (f.type === "promo_code") {
      body.promo_code = f.promo_code.trim().toUpperCase() || null;
      body.discount_type = f.discount_type;
      body.discount_amount = f.discount_amount ? Number(f.discount_amount) : null;
      body.messages = [f.messages_a, f.messages_b].filter((m) => m.trim());
      body.cta_label = f.cta_label.trim() || "Claim";
    } else {
      body.coupon_code_on_signup = f.coupon_code_on_signup.trim().toUpperCase() || null;
      body.cta_label = f.cta_label.trim() || "Unlock 10% off";
    }
    setSaving(true);
    try {
      const url = isEdit
        ? `${API}/admin/promo-slides/${initial.id}`
        : `${API}/admin/promo-slides`;
      const method = isEdit ? "patch" : "post";
      const { data } = await axios[method](url, body, {
        headers: { Authorization: `Bearer ${token}` },
      });
      onSaved(data);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="promo-slide-editor"
      className="fixed inset-0 z-[70] bg-charcoal-600/50 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-3xl w-full max-w-xl max-h-[90vh] overflow-y-auto shadow-2xl p-6"
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <p className="overline mb-1">
              {isEdit ? "Edit promo slide" : "New promo slide"}
            </p>
            <h3 className="font-display font-bold text-2xl text-charcoal-600">
              {f.headline || "Untitled slide"}
            </h3>
          </div>
          <button type="button" onClick={onClose}
                  data-testid="promo-slide-editor-close"
                  className="text-charcoal-400 hover:text-charcoal-600">
            <XIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <label className="col-span-1">
            <span className="text-xs uppercase tracking-[0.14em] text-charcoal-400 font-medium">Order</span>
            <input type="number" min="0" value={f.order} onChange={set("order")}
                   data-testid="promo-slide-order" className="input mt-1" />
          </label>
          <label className="col-span-1 flex items-end gap-2">
            <input type="checkbox" checked={f.enabled} onChange={set("enabled")}
                   data-testid="promo-slide-enabled"
                   className="w-4 h-4 accent-[#8B2252] mb-3" />
            <span className="text-sm text-charcoal-500 mb-3">Enabled</span>
          </label>
          <label className="col-span-2">
            <span className="text-xs uppercase tracking-[0.14em] text-charcoal-400 font-medium">Type</span>
            <select value={f.type} onChange={set("type")}
                    data-testid="promo-slide-type" className="input mt-1">
              <option value="promo_code">Promo Code (Claim CTA)</option>
              <option value="email_capture">Email Capture</option>
            </select>
          </label>
          <label className="col-span-2">
            <span className="text-xs uppercase tracking-[0.14em] text-charcoal-400 font-medium">Eyebrow label</span>
            <input type="text" value={f.eyebrow_label} onChange={set("eyebrow_label")}
                   placeholder="e.g. Grand Opening" data-testid="promo-slide-eyebrow"
                   className="input mt-1" />
          </label>
          <label className="col-span-2">
            <span className="text-xs uppercase tracking-[0.14em] text-charcoal-400 font-medium">Headline</span>
            <input type="text" value={f.headline} onChange={set("headline")}
                   required data-testid="promo-slide-headline"
                   className="input mt-1" />
          </label>
          <label className="col-span-2">
            <span className="text-xs uppercase tracking-[0.14em] text-charcoal-400 font-medium">Subtext (for email_capture)</span>
            <input type="text" value={f.subtext} onChange={set("subtext")}
                   data-testid="promo-slide-subtext" className="input mt-1" />
          </label>
          <label className="col-span-1">
            <span className="text-xs uppercase tracking-[0.14em] text-charcoal-400 font-medium">Icon</span>
            <select value={f.icon} onChange={set("icon")}
                    data-testid="promo-slide-icon" className="input mt-1">
              <option>Sparkles</option><option>Gift</option><option>Tag</option>
            </select>
          </label>
          <label className="col-span-1">
            <span className="text-xs uppercase tracking-[0.14em] text-charcoal-400 font-medium">Accent color</span>
            <input type="text" value={f.accent_color} onChange={set("accent_color")}
                   placeholder="#8B2252" data-testid="promo-slide-accent"
                   className="input mt-1" />
          </label>
          <label className="col-span-1">
            <span className="text-xs uppercase tracking-[0.14em] text-charcoal-400 font-medium">Start date (optional)</span>
            <input type="date" value={f.start_date} onChange={set("start_date")}
                   data-testid="promo-slide-start" className="input mt-1" />
          </label>
          <label className="col-span-1">
            <span className="text-xs uppercase tracking-[0.14em] text-charcoal-400 font-medium">End date (optional)</span>
            <input type="date" value={f.end_date} onChange={set("end_date")}
                   data-testid="promo-slide-end" className="input mt-1" />
          </label>

          {f.type === "promo_code" && (
            <>
              <label className="col-span-2">
                <span className="text-xs uppercase tracking-[0.14em] text-charcoal-400 font-medium">Promo code</span>
                <input type="text" value={f.promo_code} onChange={set("promo_code")}
                       placeholder="GRANDOPENING" data-testid="promo-slide-code"
                       className="input mt-1 uppercase tracking-wider" />
              </label>
              <label className="col-span-1">
                <span className="text-xs uppercase tracking-[0.14em] text-charcoal-400 font-medium">Discount type</span>
                <select value={f.discount_type} onChange={set("discount_type")}
                        data-testid="promo-slide-dtype" className="input mt-1">
                  <option value="percent">Percent</option>
                  <option value="amount">Dollar amount</option>
                </select>
              </label>
              <label className="col-span-1">
                <span className="text-xs uppercase tracking-[0.14em] text-charcoal-400 font-medium">Amount</span>
                <input type="number" min="0" value={f.discount_amount} onChange={set("discount_amount")}
                       data-testid="promo-slide-damount" className="input mt-1" />
              </label>
              <label className="col-span-2">
                <span className="text-xs uppercase tracking-[0.14em] text-charcoal-400 font-medium">Rotating message 1</span>
                <input type="text" value={f.messages_a} onChange={set("messages_a")}
                       data-testid="promo-slide-msg-a" className="input mt-1" />
              </label>
              <label className="col-span-2">
                <span className="text-xs uppercase tracking-[0.14em] text-charcoal-400 font-medium">Rotating message 2 (optional)</span>
                <input type="text" value={f.messages_b} onChange={set("messages_b")}
                       data-testid="promo-slide-msg-b" className="input mt-1" />
              </label>
              <label className="col-span-2">
                <span className="text-xs uppercase tracking-[0.14em] text-charcoal-400 font-medium">CTA label</span>
                <input type="text" value={f.cta_label} onChange={set("cta_label")}
                       data-testid="promo-slide-cta" className="input mt-1" />
              </label>
            </>
          )}
          {f.type === "email_capture" && (
            <>
              <label className="col-span-2">
                <span className="text-xs uppercase tracking-[0.14em] text-charcoal-400 font-medium">Coupon code granted on signup</span>
                <input type="text" value={f.coupon_code_on_signup} onChange={set("coupon_code_on_signup")}
                       placeholder="FIRST10" data-testid="promo-slide-coupon-on-signup"
                       className="input mt-1 uppercase tracking-wider" />
              </label>
              <label className="col-span-2">
                <span className="text-xs uppercase tracking-[0.14em] text-charcoal-400 font-medium">CTA label</span>
                <input type="text" value={f.cta_label} onChange={set("cta_label")}
                       data-testid="promo-slide-cta" className="input mt-1" />
              </label>
            </>
          )}
        </div>

        {err && (
          <p data-testid="promo-slide-editor-error" className="mt-4 text-sm text-red-700">{err}</p>
        )}
        <div className="mt-6 flex gap-3">
          <button type="submit" disabled={saving}
                  data-testid="promo-slide-editor-submit"
                  className="btn-primary flex-1 disabled:opacity-60">
            {saving ? "Saving…" : isEdit ? "Save changes" : "Create slide"}
          </button>
          <button type="button" onClick={onClose} className="btn-secondary flex-1">Cancel</button>
        </div>
      </form>
    </div>
  );
};


// ---------- Pricing manager panel ----------
const PricingPanel = ({ token }) => {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");
  const [editingKey, setEditingKey] = useState("");
  const [draftPrice, setDraftPrice] = useState("");
  const [acting, setActing] = useState("");
  const [successKey, setSuccessKey] = useState("");

  const fetchRows = useCallback(async () => {
    try {
      setError("");
      const { data } = await axios.get(`${API}/admin/pricing`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRows(data || []);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load pricing");
    }
  }, [token]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const beginEdit = (row) => {
    setEditingKey(`${row.product_id}-${row.variant_id}`);
    setDraftPrice(row.price.toFixed(2));
    setSuccessKey("");
    setError("");
  };
  const cancel = () => { setEditingKey(""); setDraftPrice(""); };

  const save = async (row) => {
    const key = `${row.product_id}-${row.variant_id}`;
    const newPrice = Number(draftPrice);
    if (!newPrice || newPrice <= 0) {
      setError("Price must be positive.");
      return;
    }
    if (newPrice === row.price) { cancel(); return; }
    setActing(key);
    try {
      const { data } = await axios.patch(
        `${API}/admin/pricing/${row.product_id}/${row.variant_id}`,
        { price: newPrice },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setRows((prev) => prev.map((r) =>
        r.product_id === row.product_id && r.variant_id === row.variant_id
          ? { ...r, price: data.price } : r,
      ));
      setSuccessKey(key);
      setTimeout(() => setSuccessKey(""), 2500);
      cancel();
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not update price");
    } finally {
      setActing("");
    }
  };

  if (error && !rows) {
    return <div className="surface-card p-8 text-sm text-red-700">{error}</div>;
  }
  if (rows === null) {
    return <div className="surface-card p-10 text-center text-charcoal-400 text-sm">Loading pricing…</div>;
  }

  return (
    <div data-testid="admin-pricing-panel">
      <div className="mb-5 p-4 rounded-2xl bg-cream-200/60 border border-charcoal-100 text-sm text-charcoal-500 max-w-3xl">
        <p>
          <span className="font-medium text-charcoal-600">Heads up:</span>{" "}
          Saving a price rewrites the live catalog files (<code className="text-xs">products.js</code> and{" "}
          <code className="text-xs">server.py</code>) AND persists the override in MongoDB so it
          survives container restarts. Frontend updates via hot-reload in this preview environment.
        </p>
      </div>

      {error && (
        <div data-testid="admin-pricing-error" className="mb-4 p-3 rounded-2xl bg-red-50 text-red-700 text-sm">
          {error}
        </div>
      )}

      <div className="surface-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm" data-testid="admin-pricing-table">
            <thead className="bg-cream-200/70 text-charcoal-500">
              <tr>
                <Th>Product</Th>
                <Th>Variant</Th>
                <Th>Category</Th>
                <Th className="text-right">Price (USD)</Th>
                <Th className="text-right">Actions</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const key = `${r.product_id}-${r.variant_id}`;
                const editing = editingKey === key;
                const busy = acting === key;
                return (
                  <tr
                    key={key}
                    data-testid={`pricing-row-${key}`}
                    className="border-t border-charcoal-100/40 hover:bg-cream-200/50 transition-colors"
                  >
                    <Td className="font-medium text-charcoal-600">{r.product_name}</Td>
                    <Td className="text-charcoal-500">{r.variant_label}</Td>
                    <Td className="text-charcoal-400 capitalize text-xs">{r.category.replace(/-/g, " ")}</Td>
                    <Td className="text-right">
                      {editing ? (
                        <div className="inline-flex items-center gap-1 justify-end">
                          <span className="text-charcoal-400">$</span>
                          <input
                            type="number"
                            step="0.01"
                            min="0.01"
                            value={draftPrice}
                            onChange={(e) => setDraftPrice(e.target.value)}
                            data-testid={`pricing-input-${key}`}
                            className="w-24 px-2 py-1 rounded-lg border border-charcoal-200 focus:outline-none focus:ring-2 focus:ring-[#8B2252]/40 text-right"
                            autoFocus
                          />
                        </div>
                      ) : (
                        <span className={`font-display font-bold ${successKey === key ? "text-sage-700" : "text-[#8B2252]"}`}>
                          ${r.price.toFixed(2)}
                          {successKey === key && (
                            <span className="ml-2 text-xs text-sage-700">✓ Updated</span>
                          )}
                        </span>
                      )}
                    </Td>
                    <Td className="text-right">
                      {editing ? (
                        <div className="inline-flex gap-2">
                          <button
                            type="button"
                            onClick={() => save(r)}
                            disabled={busy}
                            data-testid={`pricing-save-${key}`}
                            className="text-xs font-medium text-[#8B2252] hover:underline disabled:opacity-40"
                          >
                            {busy ? "Saving…" : "Save"}
                          </button>
                          <button
                            type="button"
                            onClick={cancel}
                            disabled={busy}
                            className="text-xs font-medium text-charcoal-400 hover:text-charcoal-600"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => beginEdit(r)}
                          data-testid={`pricing-edit-${key}`}
                          className="text-xs font-medium text-charcoal-500 hover:text-[#8B2252] inline-flex items-center gap-1"
                        >
                          <Pencil className="w-3.5 h-3.5" /> Edit
                        </button>
                      )}
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ---------- Buy & Print Label modal ----------
const BuyLabelModal = ({ token, order, onClose, onPurchased }) => {
  // USPS Small Flat Rate Box defaults — pre-filled so admin doesn't re-enter.
  const [weight, setWeight] = useState("0.5"); // pounds
  const [length, setLength] = useState("8.69");
  const [width, setWidth] = useState("5.44");
  const [height, setHeight] = useState("1.75");
  // When true, EasyPost is told this is the official USPS Small Flat Rate Box
  // (parcel = { predefined_package: "SmallFlatRateBox", weight }). USPS then
  // returns the fixed flat-rate price instead of a dim-weight-computed
  // Priority rate. Length/width/height are ignored server-side in that mode.
  const [useFlatRateBox, setUseFlatRateBox] = useState(false);
  const [rates, setRates] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [purchasing, setPurchasing] = useState(false);
  const [error, setError] = useState("");
  const [purchased, setPurchased] = useState(null);
  const [selectedCarrierId, setSelectedCarrierId] = useState(order?.shipping_carrier || "");

  const orderNumber = order?.order_number;

  const parcelPayload = () => {
    const base = {
      weight_lbs: Number(weight) || 0.5,
      length_in: Number(length) || 8.69,
      width_in: Number(width) || 5.44,
      height_in: Number(height) || 1.75,
    };
    if (useFlatRateBox) base.predefined_package = "SmallFlatRateBox";
    return base;
  };

  const fetchPreview = async () => {
    if (!orderNumber) return;
    setPreviewing(true);
    setError("");
    try {
      const { data } = await axios.post(
        `${API}/admin/orders/${orderNumber}/preview_label_rates`,
        parcelPayload(),
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setRates(data.rates || []);
      // Default to customer's choice if available
      if (!selectedCarrierId && order?.shipping_carrier) {
        setSelectedCarrierId(order.shipping_carrier);
      } else if (!selectedCarrierId && data.rates?.length) {
        setSelectedCarrierId(data.rates[0].carrier_id);
      }
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not fetch rates from EasyPost");
      setRates([]);
    } finally {
      setPreviewing(false);
    }
  };

  // Auto-fetch rates on mount AND whenever the flat-rate-box toggle flips,
  // so the customer/admin immediately sees the corrected price.
  useEffect(() => {
    if (orderNumber) fetchPreview();
  }, [orderNumber, useFlatRateBox]);  // eslint-disable-line react-hooks/exhaustive-deps

  if (!order) return null;

  const purchase = async () => {
    if (!selectedCarrierId) {
      setError("Pick a carrier first.");
      return;
    }
    setPurchasing(true);
    setError("");
    try {
      const { data } = await axios.post(
        `${API}/admin/orders/${orderNumber}/buy_label`,
        { ...parcelPayload(), carrier_id: selectedCarrierId },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setPurchased(data);
      // Give the admin 800ms to see the success state, then propagate up.
      setTimeout(() => onPurchased(data), 800);
    } catch (e) {
      setError(e?.response?.data?.detail || "Label purchase failed.");
    } finally {
      setPurchasing(false);
    }
  };

  const itemSummary = (order.items || [])
    .map((it) => `${it.product_name} ${it.variant_label} × ${it.quantity}`)
    .join(" · ");

  const shipping = order.shipping || {};
  const customerName = `${shipping.first_name || ""} ${shipping.last_name || ""}`.trim()
    || order.customer_name
    || "—";

  return (
    <div
      data-testid="buy-label-modal"
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 backdrop-blur-sm flex items-center justify-center p-4"
      style={{ background: "rgba(28, 22, 24, 0.55)" }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
        style={{ background: "#FDFAF9", color: "#1C1618" }}
      >
        {/* Header */}
        <div className="px-6 md:px-8 pt-6 pb-4 flex items-start justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-[0.18em] font-medium mb-1"
               style={{ color: "#8B2252" }}>
              Buy & Print Label · EasyPost
            </p>
            <h3 className="font-display font-bold text-2xl md:text-3xl">{order.order_number}</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={purchasing}
            data-testid="buy-label-close"
            className="text-charcoal-400 hover:text-charcoal-600 disabled:opacity-30"
          >
            <XIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Order summary */}
        <div className="px-6 md:px-8 mb-5">
          <div
            className="rounded-2xl p-4 text-sm leading-relaxed"
            style={{ background: "#F5EAE6" }}
          >
            <p className="text-[10px] uppercase tracking-[0.18em] font-medium mb-2"
               style={{ color: "#8B2252" }}>
              Ship to
            </p>
            <p className="font-display font-bold">{customerName}</p>
            <p className="text-charcoal-500">{shipping.address1}</p>
            {shipping.address2 && <p className="text-charcoal-500">{shipping.address2}</p>}
            <p className="text-charcoal-500">
              {shipping.city}, {shipping.state} {shipping.zip_code}
            </p>
            <p className="text-xs text-charcoal-400 mt-1">{shipping.phone}</p>
            <p className="text-xs text-charcoal-500 mt-3">
              <span className="font-medium">Items:</span> {itemSummary || "—"}
            </p>
            <p className="text-xs text-charcoal-500 mt-1">
              <span className="font-medium">Customer chose:</span>{" "}
              {order.shipping_method_label || (order.shipping_method === "expedited" ? "Expedited Shipping" : "Standard Shipping")}
              {" · "}
              {order.shipping_carrier_label || "USPS Priority Mail"}
            </p>
          </div>
        </div>

        {/* Parcel dimensions */}
        <div className="px-6 md:px-8 mb-5">
          <p className="text-[10px] uppercase tracking-[0.18em] text-charcoal-400 font-medium mb-3">
            Parcel
          </p>

          {/* USPS Small Flat Rate Box toggle. When on, EasyPost is told this
              is the official USPS flat-rate box and returns the fixed
              flat-rate price; length/width/height are ignored server-side. */}
          <label
            data-testid="buy-label-flat-rate-toggle"
            className={`flex items-start gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all mb-3 ${
              useFlatRateBox ? "border-[#8B2252] bg-[#F5EAE6]" : "border-charcoal-100 bg-white hover:border-[#8B2252]/40"
            }`}
          >
            <input
              type="checkbox"
              data-testid="buy-label-flat-rate-checkbox"
              checked={useFlatRateBox}
              onChange={(e) => setUseFlatRateBox(e.target.checked)}
              disabled={previewing || purchasing}
              className="mt-0.5 w-4 h-4 accent-[#8B2252]"
            />
            <div className="flex-1">
              <p className="text-sm font-medium">
                Ship in official USPS Small Flat Rate Box
              </p>
              <p className="text-xs text-charcoal-400 mt-0.5">
                Uses USPS&apos;s fixed flat-rate price (weight-only). Only tick
                this when you&apos;re packing the actual USPS-branded Small
                Flat Rate Box — otherwise leave off for cheaper dim-weight
                pricing.
              </p>
            </div>
          </label>

          <div className="grid grid-cols-4 gap-3 text-sm">
            <LabelInput label="Weight (lb)" value={weight} onChange={setWeight}
                        testId="buy-label-weight" />
            <LabelInput label="Length (in)" value={length} onChange={setLength}
                        testId="buy-label-length" disabled={useFlatRateBox} />
            <LabelInput label="Width (in)" value={width} onChange={setWidth}
                        testId="buy-label-width" disabled={useFlatRateBox} />
            <LabelInput label="Height (in)" value={height} onChange={setHeight}
                        testId="buy-label-height" disabled={useFlatRateBox} />
          </div>
          {useFlatRateBox && (
            <p
              data-testid="buy-label-flat-rate-hint"
              className="mt-2 text-[11px] text-charcoal-400"
            >
              Dimensions are ignored while flat-rate box is selected — USPS
              charges the same flat rate regardless of L × W × H.
            </p>
          )}
          <button
            type="button"
            onClick={fetchPreview}
            disabled={previewing || purchasing}
            data-testid="buy-label-refresh-rates"
            className="mt-3 text-xs font-medium underline-offset-2 hover:underline disabled:opacity-50 inline-flex items-center gap-1"
            style={{ color: "#8B2252" }}
          >
            {previewing
              ? (<><Loader2 className="w-3.5 h-3.5 animate-spin" /> Fetching live rates…</>)
              : (<><RefreshCw className="w-3.5 h-3.5" /> Refresh rates</>)}
          </button>
        </div>

        {/* Rates */}
        <div className="px-6 md:px-8 mb-5">
          <p className="text-[10px] uppercase tracking-[0.18em] text-charcoal-400 font-medium mb-3">
            Live EasyPost rates
          </p>
          {previewing && !rates ? (
            <p className="text-sm text-charcoal-400">Loading rates…</p>
          ) : !rates || rates.length === 0 ? (
            <p className="text-sm text-charcoal-400">
              No rates returned for this destination. Adjust the parcel and try again.
            </p>
          ) : (
            <div className="space-y-2" data-testid="buy-label-rates">
              {rates.map((r) => {
                const checked = selectedCarrierId === r.carrier_id;
                return (
                  <label
                    key={r.carrier_id}
                    data-testid={`buy-label-rate-${r.carrier_id}`}
                    className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all bg-white ${
                      checked ? "border-[#8B2252]" : "border-charcoal-100 hover:border-[#8B2252]/40"
                    }`}
                  >
                    <input
                      type="radio"
                      name="buy-label-carrier"
                      value={r.carrier_id}
                      checked={checked}
                      onChange={() => setSelectedCarrierId(r.carrier_id)}
                      className="w-4 h-4 accent-[#8B2252]"
                    />
                    <div className="flex-1">
                      <p className="text-sm font-medium">{r.carrier_label}</p>
                      <p className="text-xs text-charcoal-400">
                        {r.estimated_days}
                        {r.carrier_id === order.shipping_carrier && (
                          <span className="ml-2 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full text-white"
                                style={{ background: "#8B2252" }}>Customer&apos;s choice</span>
                        )}
                      </p>
                    </div>
                    <span className="font-display font-bold text-lg" style={{ color: "#8B2252" }}>
                      ${r.rate.toFixed(2)}
                    </span>
                  </label>
                );
              })}
            </div>
          )}
        </div>

        {error && (
          <div data-testid="buy-label-error" className="px-6 md:px-8 mb-4">
            <div className="p-3 rounded-2xl bg-red-50 text-red-700 text-sm">{error}</div>
          </div>
        )}

        {purchased ? (
          <div data-testid="buy-label-success" className="px-6 md:px-8 pb-6 md:pb-8">
            <div className="p-4 rounded-2xl border-2"
                 style={{ borderColor: "#8B2252", background: "#F5EAE6" }}>
              <p className="font-display font-bold text-xl mb-1">Label purchased</p>
              <p className="text-sm mb-2">
                Tracking: <span className="font-medium select-all">{purchased.tracking_number}</span>
              </p>
              <p className="text-xs text-charcoal-500">
                Order marked Shipped/Fulfilled. The customer has been emailed the tracking number.
                Your label is opening in a new tab — send it to your Rollo or Dymo 4XL.
              </p>
              {purchased.easypost_postage_label_url && (
                <a
                  href={purchased.easypost_postage_label_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  download
                  data-testid="buy-label-download-link"
                  className="mt-3 inline-flex items-center gap-2 text-sm font-medium hover:underline"
                  style={{ color: "#8B2252" }}
                >
                  <Download className="w-4 h-4" strokeWidth={2} />
                  Download Label (PDF)
                </a>
              )}
            </div>
          </div>
        ) : (
          <div className="px-6 md:px-8 pb-6 md:pb-8 flex flex-wrap gap-3">
            <button
              type="button"
              onClick={purchase}
              disabled={purchasing || previewing || !selectedCarrierId || !rates?.length}
              data-testid="buy-label-purchase"
              className="inline-flex items-center justify-center px-6 py-3 rounded-full font-medium text-white transition-all hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ background: "#8B2252" }}
            >
              {purchasing
                ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" strokeWidth={2} /> Purchasing label…</>)
                : (<><Printer className="w-4 h-4 mr-2" strokeWidth={2} /> Purchase Label</>)}
            </button>
            <button
              type="button"
              onClick={onClose}
              disabled={purchasing}
              className="btn-secondary disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const LabelInput = ({ label, value, onChange, testId, disabled = false }) => (
  <label className={`block ${disabled ? "opacity-50" : ""}`}>
    <span className="block text-[10px] uppercase tracking-[0.18em] text-charcoal-400 font-medium mb-1.5">
      {label}
    </span>
    <input
      type="number"
      step="0.01"
      min="0.01"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      data-testid={testId}
      className="w-full px-3 py-2 rounded-lg border border-charcoal-200 bg-white focus:outline-none focus:ring-2 focus:ring-[#8B2252]/30 focus:border-[#8B2252] text-sm disabled:bg-cream-200/60 disabled:cursor-not-allowed"
    />
  </label>
);


// ---------- Shipping settings panel ----------
const ShippingSettingsPanel = ({ token }) => {
  const [settings, setSettings] = useState(null);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);

  // Editable inputs (strings so the user can clear/type freely)
  const [standardPrice, setStandardPrice] = useState("");
  const [freeThreshold, setFreeThreshold] = useState("");
  const [freeShippingEnabled, setFreeShippingEnabled] = useState(false);

  const fetchSettings = useCallback(async () => {
    setError("");
    try {
      const { data } = await axios.get(`${API}/admin/shipping/settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSettings(data);
      setStandardPrice(String(data.standard_price));
      setFreeThreshold(String(data.free_threshold));
      setFreeShippingEnabled(!!data.free_shipping_enabled);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load shipping settings");
    }
  }, [token]);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const save = async () => {
    setError(""); setSuccess(false);
    const sp = Number(standardPrice);
    const ft = Number(freeThreshold);
    if (Number.isNaN(sp) || sp < 0) {
      setError("Standard price must be a non-negative number.");
      return;
    }
    if (Number.isNaN(ft) || ft < 0) {
      setError("Free-shipping threshold must be a non-negative number.");
      return;
    }
    setSaving(true);
    try {
      const { data } = await axios.patch(
        `${API}/admin/shipping/settings`,
        {
          standard_price: sp,
          free_threshold: ft,
          free_shipping_enabled: freeShippingEnabled,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setSettings((prev) => ({ ...(prev || {}), ...data }));
      setStandardPrice(String(data.standard_price));
      setFreeThreshold(String(data.free_threshold));
      setFreeShippingEnabled(!!data.free_shipping_enabled);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 2500);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not save shipping settings");
    } finally {
      setSaving(false);
    }
  };

  const resetToDefault = () => {
    if (!settings?.defaults) return;
    setStandardPrice(String(settings.defaults.standard_price));
    setFreeThreshold(String(settings.defaults.free_threshold));
    setFreeShippingEnabled(false);
  };

  if (!settings && !error) {
    return <div className="surface-card p-10 text-center text-charcoal-400 text-sm">Loading shipping settings…</div>;
  }
  if (!settings && error) {
    return <div className="surface-card p-8 text-sm text-red-700" data-testid="shipping-settings-error">{error}</div>;
  }

  const dirty =
    Number(standardPrice) !== Number(settings.standard_price) ||
    Number(freeThreshold) !== Number(settings.free_threshold) ||
    Boolean(freeShippingEnabled) !== Boolean(settings.free_shipping_enabled);

  return (
    <div data-testid="admin-shipping-panel" className="max-w-2xl">
      <div className="mb-5 p-4 rounded-2xl bg-cream-200/60 border border-charcoal-100 text-sm text-charcoal-500">
        <p>
          <span className="font-medium text-charcoal-600">Live shipping rules:</span>{" "}
          {settings.free_shipping_enabled ? (
            <>
              <span className="font-medium text-sage-700">Free shipping is currently ON</span> —
              every order ships free regardless of subtotal.
            </>
          ) : (
            <>
              Customer pays <strong>${Number(settings.standard_price).toFixed(2)}</strong> for Standard shipping,
              or free once subtotal reaches <strong>${Number(settings.free_threshold).toFixed(2)}</strong>.
            </>
          )}
          {" "}Changes here apply to new orders immediately — no redeploy required.
        </p>
      </div>

      {error && (
        <div data-testid="shipping-settings-inline-error" className="mb-4 p-3 rounded-2xl bg-red-50 text-red-700 text-sm">
          {error}
        </div>
      )}

      <div className="surface-card p-6 md:p-7 space-y-5">
        {/* Global free-shipping toggle. When ON, the standard price + free
            threshold below are ignored at checkout time. */}
        <label
          htmlFor="ship-free-toggle"
          className={`flex items-center justify-between gap-4 rounded-2xl border-2 p-4 cursor-pointer transition-colors ${
            freeShippingEnabled
              ? "border-sage-500 bg-sage-50"
              : "border-charcoal-100 bg-cream-100 hover:bg-cream-200/40"
          }`}
        >
          <span>
            <span className="font-display font-bold text-charcoal-600 block">
              Free shipping (all orders)
            </span>
            <span className="text-xs text-charcoal-400">
              When ON, every order ships free regardless of subtotal. Overrides the standard price + threshold below.
            </span>
          </span>
          <span
            className={`relative inline-flex h-6 w-11 shrink-0 rounded-full transition-colors ${
              freeShippingEnabled ? "bg-sage-600" : "bg-charcoal-200"
            }`}
          >
            <input
              id="ship-free-toggle"
              type="checkbox"
              checked={freeShippingEnabled}
              onChange={(e) => setFreeShippingEnabled(e.target.checked)}
              data-testid="shipping-free-toggle"
              className="sr-only"
            />
            <span
              className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${
                freeShippingEnabled ? "translate-x-5" : "translate-x-0.5"
              }`}
            />
          </span>
        </label>
        <label className="block">
          <span className="block text-xs font-medium uppercase tracking-[0.18em] text-charcoal-400 mb-2">
            Standard shipping price (USD)
          </span>
          <div className="flex items-center gap-2">
            <span className="text-charcoal-400">$</span>
            <input
              type="number" step="0.01" min="0"
              value={standardPrice}
              onChange={(e) => setStandardPrice(e.target.value)}
              data-testid="shipping-standard-price"
              className="input w-40"
            />
          </div>
          <p className="text-xs text-charcoal-400 mt-1.5">
            Default: ${Number(settings.defaults?.standard_price ?? 14).toFixed(2)}
          </p>
        </label>

        <label className="block">
          <span className="block text-xs font-medium uppercase tracking-[0.18em] text-charcoal-400 mb-2">
            Free-shipping threshold (USD)
          </span>
          <div className="flex items-center gap-2">
            <span className="text-charcoal-400">$</span>
            <input
              type="number" step="0.01" min="0"
              value={freeThreshold}
              onChange={(e) => setFreeThreshold(e.target.value)}
              data-testid="shipping-free-threshold"
              className="input w-40"
            />
          </div>
          <p className="text-xs text-charcoal-400 mt-1.5">
            Default: ${Number(settings.defaults?.free_threshold ?? 300).toFixed(2)}
          </p>
        </label>

        <div className="flex flex-wrap items-center gap-3 pt-2">
          <button
            type="button"
            onClick={save}
            disabled={saving || !dirty}
            data-testid="shipping-settings-save"
            className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? "Saving…" : "Save changes"}
          </button>
          <button
            type="button"
            onClick={resetToDefault}
            disabled={saving}
            data-testid="shipping-settings-reset"
            className="text-xs font-medium text-charcoal-500 hover:text-[#8B2252] underline-offset-2 hover:underline disabled:opacity-50"
          >
            Reset to defaults
          </button>
          {success && (
            <span data-testid="shipping-settings-success" className="text-xs text-sage-700 font-medium">
              ✓ Saved — live on checkout immediately
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

